import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  FlaskConical, Plus, ArrowLeft, ArrowRight, Trash2, Copy, Save, Package,
  Search, AlertCircle, Check, GitCompare, FileText, Download, Eye, AlertTriangle,
  Box, Layers, Users, DollarSign, Settings, FileDown, FileUp, Calculator,
  ChevronUp, ChevronDown, TrendingUp, Target, LineChart, GripVertical,
  Send, CheckCircle, XCircle, Clock, History, Filter, Upload, Paperclip, Building2,
  MoreHorizontal, ChevronRight, Percent
} from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { API } from "@/config";

// Fixed conversion factors (grams per UOM) - these are physical constants
const FIXED_CONVERSIONS = {
  "lb": 453.592,
  "kg": 1000,
  "oz": 28.3495,
  "g": 1
};

const COST_UOMS = ["lb", "kg", "oz", "g", "each", "case", "gal", "L", "ml"];

// FDA 9 Major Allergens with detection keywords
const ALLERGEN_CONFIG = [
  { id: "milk", label: "Milk", keywords: ["milk", "cream", "butter", "cheese", "whey", "casein", "lactose", "dairy"] },
  { id: "eggs", label: "Eggs", keywords: ["egg", "eggs", "albumin", "globulin", "lysozyme", "mayonnaise", "meringue"] },
  { id: "fish", label: "Fish", keywords: ["fish", "cod", "salmon", "tuna", "anchovy", "bass", "catfish", "flounder"] },
  { id: "shellfish", label: "Shellfish", keywords: ["shellfish", "shrimp", "crab", "lobster", "crawfish", "prawn", "scallop", "clam", "mussel", "oyster"] },
  { id: "tree_nuts", label: "Tree Nuts", keywords: ["almond", "cashew", "walnut", "pecan", "pistachio", "macadamia", "hazelnut", "chestnut", "brazil nut"] },
  { id: "peanuts", label: "Peanuts", keywords: ["peanut", "peanuts", "groundnut", "arachis"] },
  { id: "wheat", label: "Wheat", keywords: ["wheat", "flour", "bread", "bulgur", "couscous", "durum", "semolina", "spelt", "gluten"] },
  { id: "soybeans", label: "Soybeans", keywords: ["soy", "soybean", "soya", "edamame", "miso", "tofu", "tempeh"] },
  { id: "sesame", label: "Sesame", keywords: ["sesame", "tahini", "halvah"] }
];

// Auto-detect allergens from ingredient statement
const detectAllergens = (ingredientStatement) => {
  if (!ingredientStatement) return { detected: {}, hasAutoDetected: false };
  
  const lowerText = ingredientStatement.toLowerCase();
  const detected = {};
  let hasAutoDetected = false;
  
  ALLERGEN_CONFIG.forEach(allergen => {
    const found = allergen.keywords.some(keyword => lowerText.includes(keyword));
    if (found) {
      detected[allergen.id] = true;
      hasAutoDetected = true;
    }
  });
  
  return { detected, hasAutoDetected };
};

// Mock data fallback
const MOCK_ITEM = {
  id: "item-001", project_id: "proj-001", project_name: "Organic Breakfast Line",
  name: "Organic Breakfast Burrito", status: "active", target_cost: 2.50
};

const MOCK_FORMULATIONS = [
  { id: "form-001", version: "v1", name: "Initial Recipe", is_active: true, created_at: "2024-10-20", total_cost: 2.85 }
];

const MOCK_FORMULATION_LINES = [
  { id: "line-001", item_id: "existing-001", item_name: "Organic Flour Tortilla", item_type: "existing", bom_qty_grams: 65, yield_percent: 100, cost_uom: "each", conversion_factor: 65, cost_per_uom: 0.35, category: "Wrapper", allergens: ["Wheat"], ingredient_statement: "" },
  { id: "line-002", item_id: "existing-002", item_name: "Scrambled Eggs", item_type: "existing", bom_qty_grams: 85, yield_percent: 95, cost_uom: "lb", conversion_factor: 453.592, cost_per_uom: 2.50, category: "Protein", allergens: ["Eggs"], ingredient_statement: "Whole Eggs, Skim Milk, Salt" }
];

const MOCK_EXISTING_ITEMS = [
  { id: "existing-001", name: "Organic Flour Tortilla", sku: "TORT-ORG-10", category: "Wrapper" },
  { id: "existing-002", name: "Scrambled Eggs", sku: "EGG-SCRM-001", category: "Protein" }
];

// Sortable Table Row Component for drag and drop
const SortableTableRow = ({ id, children, className = "" }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? '#f3f4f6' : undefined,
  };

  return (
    <tr ref={setNodeRef} style={style} className={className}>
      <td className="px-1 py-1 w-6">
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded">
          <GripVertical className="w-3 h-3 text-gray-400" />
        </div>
      </td>
      {children}
    </tr>
  );
};

const RDItemDetailPage = () => {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [project, setProject] = useState(null);  // Project with customer info
  const [relatedItems, setRelatedItems] = useState([]);  // Other items in same project
  const [customers, setCustomers] = useState([]);  // CRM customers for dropdown
  const [brokers, setBrokers] = useState([]);  // CRM brokers for dropdown
  const [formulations, setFormulations] = useState([]);
  const [activeFormulation, setActiveFormulation] = useState(null);
  const [formulationLines, setFormulationLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddLineModal, setShowAddLineModal] = useState(false);
  const [showCreatePendingModal, setShowCreatePendingModal] = useState(false);
  const [showNewVersionModal, setShowNewVersionModal] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [showNutritionModal, setShowNutritionModal] = useState(false);
  const [showIngredientDetailModal, setShowIngredientDetailModal] = useState(false);
  const [selectedIngredient, setSelectedIngredient] = useState(null);
  const [existingItems, setExistingItems] = useState([]);
  const [itemSearch, setItemSearch] = useState("");
  const [nutritionData, setNutritionData] = useState(null);
  const [nutritionLoading, setNutritionLoading] = useState(false);
  const [servingSize, setServingSize] = useState({ grams: 0, description: "1 serving" });
  const [selectedLabelFormat, setSelectedLabelFormat] = useState("vertical");
  
  // File upload states
  const [files, setFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [summaryText, setSummaryText] = useState("");
  
  // USDA Quick Import states
  const [usdaSearchIngredient, setUsdaSearchIngredient] = useState(null);
  const [usdaQuickResults, setUsdaQuickResults] = useState([]);
  const [usdaQuickLoading, setUsdaQuickLoading] = useState(false);
  const [usdaImporting, setUsdaImporting] = useState(null);
  
  const [compareFormulations, setCompareFormulations] = useState([]);
  const [compareData, setCompareData] = useState({});
  const [showCostHistoryChart, setShowCostHistoryChart] = useState(true);
  
  // Packaging states
  const [showPackagingSection, setShowPackagingSection] = useState(false);
  const [packagingComponents, setPackagingComponents] = useState([]);
  const [packagingConfig, setPackagingConfig] = useState({
    has_carton_level: true,
    units_per_carton: 6,
    cartons_per_case: 4,
    units_per_case: 24,
    cases_per_pallet: 60
  });
  const [packagingLines, setPackagingLines] = useState([]);
  const [packagingCostBreakdown, setPackagingCostBreakdown] = useState(null);
  const [showAddPackagingModal, setShowAddPackagingModal] = useState(false);
  const [showCreatePackagingComponentModal, setShowCreatePackagingComponentModal] = useState(false);
  const [showCopyPackagingModal, setShowCopyPackagingModal] = useState(false);
  const [rdItemsForCopy, setRdItemsForCopy] = useState([]);
  const [copyTargetItemId, setCopyTargetItemId] = useState('');
  const [copySearchTerm, setCopySearchTerm] = useState('');
  const [newPackagingComponent, setNewPackagingComponent] = useState({
    name: '',
    description: '',
    level: 'unit',
    cost: 0,
    supplier: '',
    sku: ''
  });
  
  // Packaging Templates states
  const [packagingTemplates, setPackagingTemplates] = useState([]);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [showLoadTemplateModal, setShowLoadTemplateModal] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');
  
  // Labor Costing states
  const [showLaborSection, setShowLaborSection] = useState(false);
  const [laborSettings, setLaborSettings] = useState({
    hours_per_shift: 8.5,
    tax_burden_multiplier: 1.0765,
    default_wage_rates: {}
  });
  const [laborLines, setLaborLines] = useState([]);
  const [throughputUnitsPerShift, setThroughputUnitsPerShift] = useState(0);
  const [laborCostBreakdown, setLaborCostBreakdown] = useState(null);
  const [showAddLaborLineModal, setShowAddLaborLineModal] = useState(false);
  const [showLaborSettingsModal, setShowLaborSettingsModal] = useState(false);
  const [newLaborLine, setNewLaborLine] = useState({
    employee_type: '',
    employee_count: 1,
    hourly_rate: 0
  });
  
  // Throughput states
  const [throughputConfig, setThroughputConfig] = useState({
    units_per_minute: 0,
    efficiency_rate: 85,
    hours_per_shift: 8,
    calculated_throughput: 0
  });
  
  // Miscellaneous Costs states
  const [showMiscCostsSection, setShowMiscCostsSection] = useState(false);
  const [showThroughputSection, setShowThroughputSection] = useState(true);
  const [miscCostTypes, setMiscCostTypes] = useState([]);
  const [miscCostLines, setMiscCostLines] = useState([]);
  const [miscCostBreakdown, setMiscCostBreakdown] = useState(null);
  const [showAddMiscCostModal, setShowAddMiscCostModal] = useState(false);
  const [newMiscCostLine, setNewMiscCostLine] = useState({
    cost_type: '',
    cost_method: 'per_unit',
    cost_value: 0
  });
  
  // Pricing & Total Cost states
  const [showTotalCostSection, setShowTotalCostSection] = useState(true);
  const [pricingConfig, setPricingConfig] = useState({
    pricing_strategy: 'tolling',
    overhead_per_shift: 10000,
    turnkey_price: 0
  });
  const [totalCostBreakdown, setTotalCostBreakdown] = useState(null);
  
  // Activity Log states
  const [activityLog, setActivityLog] = useState([]);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [activityFilter, setActivityFilter] = useState('all');
  
  // Review workflow states
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewAction, setReviewAction] = useState(''); // submit, approve, reject
  const [reviewNotes, setReviewNotes] = useState('');
  
  // Unsaved changes tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Initialize allergens as object with boolean values
  const emptyAllergens = () => ALLERGEN_CONFIG.reduce((acc, a) => ({ ...acc, [a.id]: false }), {});
  
  const [newLine, setNewLine] = useState({
    item_id: "", item_name: "", item_type: "existing",
    bom_qty_grams: "", yield_percent: 100, cost_uom: "lb",
    conversion_factor: 453.592, cost_per_uom: "", category: "", 
    allergens: emptyAllergens(), ingredient_statement: "", allergens_auto_detected: false
  });

  const [newPendingItem, setNewPendingItem] = useState({
    name: "", sku: "", description: "", category: "",
    item_type: "raw_material", estimated_cost: "", cost_uom: "lb",
    supplier_name: "", notes: "", allergens: emptyAllergens(), ingredient_statement: "", allergens_auto_detected: false
  });

  const [newVersion, setNewVersion] = useState({ name: "", notes: "" });

  // Calculate sidebar width for fixed header offset (default 200px, collapsed 60px)
  const [sidebarWidth, setSidebarWidth] = useState(200);
  
  useEffect(() => {
    const checkSidebar = () => {
      const sidebar = document.querySelector('.side-nav');
      if (sidebar) {
        setSidebarWidth(sidebar.classList.contains('side-nav-collapsed') ? 60 : 200);
      }
    };
    checkSidebar();
    // Re-check on window resize
    window.addEventListener('resize', checkSidebar);
    // MutationObserver to detect class changes on sidebar
    const sidebar = document.querySelector('.side-nav');
    let observer;
    if (sidebar) {
      observer = new MutationObserver(checkSidebar);
      observer.observe(sidebar, { attributes: true, attributeFilter: ['class'] });
    }
    return () => {
      window.removeEventListener('resize', checkSidebar);
      if (observer) observer.disconnect();
    };
  }, []);

  useEffect(() => { fetchItemData(); }, [itemId]);

  const fetchItemData = async () => {
    try {
      const [itemRes, formulationsRes, existingRes, customersRes, brokersRes] = await Promise.all([
        axios.get(`${API}/rd/items/${itemId}`),
        axios.get(`${API}/rd/items/${itemId}/formulations`),
        axios.get(`${API}/items`),
        axios.get(`${API}/crm/customers`),
        axios.get(`${API}/crm/brokers`)
      ]);
      setItem(itemRes.data);
      setFormulations(formulationsRes.data);
      setExistingItems(existingRes.data);
      setCustomers(customersRes.data || []);
      setBrokers(brokersRes.data || []);
      
      const active = formulationsRes.data.find(f => f.is_active);
      if (active) {
        setActiveFormulation(active);
        fetchFormulationLines(active.id);
      }
      
      // Fetch project data with customer info
      if (itemRes.data.project_id) {
        try {
          const projectRes = await axios.get(`${API}/rd/projects/${itemRes.data.project_id}`);
          setProject(projectRes.data);
          
          // Fetch related items in same project
          const itemsRes = await axios.get(`${API}/rd/projects/${itemRes.data.project_id}/items`);
          setRelatedItems(itemsRes.data || []);
        } catch (e) {
          console.error('Failed to fetch project data:', e);
        }
      }
      
      // Fetch files
      fetchFiles();
      // Set summary text
      setSummaryText(itemRes.data.summary || '');
      
      // Fetch packaging data
      fetchPackagingData();
      // Fetch labor data
      fetchLaborData();
      // Fetch packaging templates
      fetchPackagingTemplates();
    } catch (error) {
      setItem(MOCK_ITEM);
      setFormulations(MOCK_FORMULATIONS);
      setExistingItems(MOCK_EXISTING_ITEMS);
      const active = MOCK_FORMULATIONS.find(f => f.is_active);
      if (active) {
        setActiveFormulation(active);
        setFormulationLines(MOCK_FORMULATION_LINES);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchFiles = async () => {
    try {
      const res = await axios.get(`${API}/rd/items/${itemId}/files`);
      setFiles(res.data.files || []);
    } catch (error) {
      console.error('Failed to fetch files:', error);
    }
  };

  const fetchActivityLog = async () => {
    try {
      const params = activityFilter !== 'all' ? `?activity_type=${activityFilter}` : '';
      const res = await axios.get(`${API}/rd/items/${itemId}/activity${params}`);
      setActivityLog(res.data.activities || []);
    } catch (error) {
      console.error('Failed to fetch activity log:', error);
    }
  };

  // Fetch activity log when filter changes or when showing activity log
  useEffect(() => {
    if (showActivityLog) {
      fetchActivityLog();
    }
  }, [showActivityLog, activityFilter, itemId]);

  // Handle customer change - updates the project
  const handleCustomerChange = async (customerId) => {
    if (!project) return;
    
    try {
      const selectedCustomer = customers.find(c => c.id === customerId);
      
      // Build update data including customer's broker
      const updateData = {
        customer_id: customerId || null,
        customer_name: selectedCustomer?.name || null,
        // Auto-set broker from customer (broker flows from customer to project)
        broker_id: selectedCustomer?.broker_id || null,
        broker_name: selectedCustomer?.broker_name || null,
        broker_rate: null  // Will use default from broker record
      };
      
      // If customer has a broker, get the default rate from broker record
      if (selectedCustomer?.broker_id) {
        const linkedBroker = brokers.find(b => b.id === selectedCustomer.broker_id);
        if (linkedBroker) {
          updateData.broker_rate = linkedBroker.commission_rate;
        }
      }
      
      await axios.put(`${API}/rd/projects/${project.id}`, updateData);
      setProject(prev => ({ ...prev, ...updateData }));
      
      if (selectedCustomer) {
        if (selectedCustomer.broker_name) {
          toast.success(`Customer set to ${selectedCustomer.name} (Broker: ${selectedCustomer.broker_name})`);
        } else {
          toast.success(`Customer set to ${selectedCustomer.name}`);
        }
      } else {
        toast.success('Customer removed');
      }
    } catch (error) {
      console.error('Failed to update customer:', error);
      toast.error('Failed to update customer');
    }
  };

  // Get the default broker rate from the broker record
  const getDefaultBrokerRate = () => {
    if (!project?.broker_id) return null;
    const broker = brokers.find(b => b.id === project.broker_id);
    return broker?.commission_rate || null;
  };

  // Get the effective brokerage rate for this item (item override > project rate > default)
  const getEffectiveBrokerRate = () => {
    // Item-level override takes precedence
    if (item?.broker_rate_override !== null && item?.broker_rate_override !== undefined) {
      return item.broker_rate_override;
    }
    // Fall back to project rate
    return project?.broker_rate || 0;
  };

  // Check if project rate is different from broker's default rate
  const isProjectRateNonStandard = () => {
    const defaultRate = getDefaultBrokerRate();
    if (defaultRate === null || project?.broker_rate === null) return false;
    return Math.abs(defaultRate - project.broker_rate) > 0.01;
  };

  // Check if item has its own rate override (different from project rate)
  const hasItemRateOverride = () => {
    return item?.broker_rate_override !== null && item?.broker_rate_override !== undefined;
  };

  // Handle brokerage rate change - now at ITEM level
  const handleItemBrokerRateChange = async (newRate) => {
    if (!item) return;
    
    try {
      const projectRate = project?.broker_rate || 0;
      
      // If rate matches project rate, clear the item override
      const shouldClearOverride = Math.abs(projectRate - newRate) < 0.01;
      const updateValue = shouldClearOverride ? null : newRate;
      
      await axios.put(`${API}/rd/items/${item.id}`, { broker_rate_override: updateValue });
      setItem(prev => ({ ...prev, broker_rate_override: updateValue }));
      
      if (shouldClearOverride) {
        toast.success(`Using project brokerage rate (${projectRate}%)`);
      } else {
        toast.success(`Item brokerage rate set to ${newRate}% (project: ${projectRate}%)`);
      }
    } catch (error) {
      console.error('Failed to update item broker rate:', error);
      toast.error('Failed to update brokerage rate');
    }
  };

  // Reset item rate to project rate
  const resetToProjectRate = async () => {
    if (!item) return;
    
    try {
      await axios.put(`${API}/rd/items/${item.id}`, { broker_rate_override: null });
      setItem(prev => ({ ...prev, broker_rate_override: null }));
      toast.success(`Reset to project brokerage rate (${project?.broker_rate || 0}%)`);
    } catch (error) {
      console.error('Failed to reset broker rate:', error);
      toast.error('Failed to reset brokerage rate');
    }
  };

  const fetchPackagingData = async () => {
    try {
      const [componentsRes, itemPackagingRes] = await Promise.all([
        axios.get(`${API}/rd/packaging/components`),
        axios.get(`${API}/rd/items/${itemId}/packaging`)
      ]);
      setPackagingComponents(componentsRes.data);
      if (itemPackagingRes.data.config) {
        setPackagingConfig(itemPackagingRes.data.config);
      }
      if (itemPackagingRes.data.lines) {
        setPackagingLines(itemPackagingRes.data.lines);
      }
    } catch (error) {
      console.error('Failed to fetch packaging data:', error);
    }
  };

  const fetchPackagingTemplates = async () => {
    try {
      const response = await axios.get(`${API}/rd/packaging/templates`);
      setPackagingTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch packaging templates:', error);
    }
  };

  const fetchLaborData = async () => {
    try {
      const [settingsRes, laborRes, throughputRes, miscTypesRes, miscCostsRes, pricingRes] = await Promise.all([
        axios.get(`${API}/rd/settings/labor`),
        axios.get(`${API}/rd/items/${itemId}/labor`),
        axios.get(`${API}/rd/items/${itemId}/throughput`),
        axios.get(`${API}/rd/settings/misc-cost-types`),
        axios.get(`${API}/rd/items/${itemId}/misc-costs`),
        axios.get(`${API}/rd/items/${itemId}/pricing`)
      ]);
      setLaborSettings(settingsRes.data);
      if (laborRes.data.lines) {
        setLaborLines(laborRes.data.lines);
      }
      if (laborRes.data.throughput_units_per_shift) {
        setThroughputUnitsPerShift(laborRes.data.throughput_units_per_shift);
      }
      if (throughputRes.data) {
        setThroughputConfig(throughputRes.data);
        if (throughputRes.data.calculated_throughput > 0) {
          setThroughputUnitsPerShift(throughputRes.data.calculated_throughput);
        }
      }
      setMiscCostTypes(miscTypesRes.data);
      if (miscCostsRes.data.lines) {
        setMiscCostLines(miscCostsRes.data.lines);
      }
      if (pricingRes.data) {
        setPricingConfig(pricingRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch labor data:', error);
    }
  };

  const calculatePackagingCost = async () => {
    try {
      const response = await axios.post(`${API}/rd/items/${itemId}/packaging/calculate`, {
        config: packagingConfig,
        lines: packagingLines
      });
      setPackagingCostBreakdown(response.data);
    } catch (error) {
      console.error('Failed to calculate packaging cost:', error);
    }
  };

  useEffect(() => {
    if (packagingLines.length > 0 || showPackagingSection) {
      calculatePackagingCost();
    }
  }, [packagingConfig, packagingLines, showPackagingSection]);

  const handleSavePackaging = async () => {
    try {
      await axios.put(`${API}/rd/items/${itemId}/packaging`, {
        config: packagingConfig,
        lines: packagingLines
      });
      toast.success("Packaging configuration saved");
    } catch (error) {
      console.error('Failed to save packaging:', error);
      toast.error("Failed to save packaging");
    }
  };

  const handleOpenAddPackagingModal = async () => {
    // Ensure components are loaded
    if (packagingComponents.length === 0) {
      try {
        const response = await axios.get(`${API}/rd/packaging/components`);
        setPackagingComponents(response.data);
      } catch (error) {
        console.error('Failed to fetch packaging components:', error);
      }
    }
    setShowAddPackagingModal(true);
  };

  const handleAddPackagingLine = (component) => {
    const newLine = {
      id: `pkg-line-${Date.now()}`,
      component_id: component.id,
      component_name: component.name,
      level: component.level,
      cost_at_level: component.cost,
      quantity: 1,
      yield_percent: 100
    };
    setPackagingLines(prev => [...prev, newLine]);
    setShowAddPackagingModal(false);
  };

  const handleUpdatePackagingLine = (lineId, field, value) => {
    setPackagingLines(prev => prev.map(line =>
      line.id === lineId ? { ...line, [field]: value } : line
    ));
  };

  const handleDeletePackagingLine = (lineId) => {
    setPackagingLines(prev => prev.filter(line => line.id !== lineId));
  };

  // Create new packaging component
  const handleCreatePackagingComponent = async () => {
    if (!newPackagingComponent.name.trim()) {
      toast.error("Component name is required");
      return;
    }
    try {
      const response = await axios.post(`${API}/rd/packaging/components`, newPackagingComponent);
      const createdComponent = response.data;
      setPackagingComponents(prev => [...prev, createdComponent]);
      // Auto-add to current item
      handleAddPackagingLine(createdComponent);
      setShowCreatePackagingComponentModal(false);
      setNewPackagingComponent({ name: '', description: '', level: 'unit', cost: 0, supplier: '', sku: '' });
      toast.success(`${createdComponent.name} created and added`);
    } catch (error) {
      console.error('Failed to create packaging component:', error);
      toast.error("Failed to create component");
    }
  };

  // Fetch R&D items for copy modal
  const fetchRdItemsForCopy = async () => {
    try {
      const response = await axios.get(`${API}/rd/projects`);
      const projects = response.data;
      // Fetch items for each project
      const allItems = [];
      for (const project of projects) {
        try {
          const itemsRes = await axios.get(`${API}/rd/projects/${project.id}/items`);
          itemsRes.data.forEach(item => {
            if (item.id !== itemId) { // Exclude current item
              allItems.push({ ...item, project_name: project.name });
            }
          });
        } catch (e) {}
      }
      setRdItemsForCopy(allItems);
    } catch (error) {
      console.error('Failed to fetch R&D items:', error);
    }
  };

  // Copy packaging config to another item
  const handleCopyPackagingToItem = async () => {
    if (!copyTargetItemId) {
      toast.error("Please select a target item");
      return;
    }
    try {
      await axios.put(`${API}/rd/items/${copyTargetItemId}/packaging`, {
        config: packagingConfig,
        lines: packagingLines.map(line => ({
          ...line,
          id: `pkg-line-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        }))
      });
      setShowCopyPackagingModal(false);
      setCopyTargetItemId('');
      setCopySearchTerm('');
      toast.success("Packaging configuration copied successfully");
    } catch (error) {
      console.error('Failed to copy packaging:', error);
      toast.error("Failed to copy packaging configuration");
    }
  };

  const handleOpenCopyPackagingModal = () => {
    fetchRdItemsForCopy();
    setShowCopyPackagingModal(true);
  };

  const filteredRdItemsForCopy = rdItemsForCopy.filter(item =>
    item.name.toLowerCase().includes(copySearchTerm.toLowerCase()) ||
    item.project_name?.toLowerCase().includes(copySearchTerm.toLowerCase())
  );

  // ============ PACKAGING TEMPLATES ============
  
  const handleSaveAsTemplate = async () => {
    if (!newTemplateName.trim()) {
      toast.error("Template name is required");
      return;
    }
    try {
      const response = await axios.post(`${API}/rd/packaging/templates`, {
        name: newTemplateName,
        description: newTemplateDescription,
        config: packagingConfig,
        lines: packagingLines
      });
      setPackagingTemplates(prev => [...prev, response.data]);
      setShowSaveTemplateModal(false);
      setNewTemplateName('');
      setNewTemplateDescription('');
      toast.success(`Template "${response.data.name}" saved`);
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error("Failed to save template");
    }
  };

  const handleLoadTemplate = async (templateId) => {
    try {
      const response = await axios.post(`${API}/rd/items/${itemId}/packaging/apply-template/${templateId}`);
      setPackagingConfig(response.data.config);
      setPackagingLines(response.data.lines);
      setShowLoadTemplateModal(false);
      toast.success("Template applied successfully");
    } catch (error) {
      console.error('Failed to apply template:', error);
      toast.error("Failed to apply template");
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await axios.delete(`${API}/rd/packaging/templates/${templateId}`);
      setPackagingTemplates(prev => prev.filter(t => t.id !== templateId));
      toast.success("Template deleted");
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  // ============ LABOR COSTING ============

  const calculateLaborCost = async () => {
    if (laborLines.length === 0) return;
    try {
      const response = await axios.post(`${API}/rd/items/${itemId}/labor/calculate`, {
        lines: laborLines,
        throughput_units_per_shift: throughputUnitsPerShift
      });
      setLaborCostBreakdown(response.data);
    } catch (error) {
      console.error('Failed to calculate labor cost:', error);
    }
  };

  useEffect(() => {
    if (laborLines.length > 0 || showLaborSection) {
      calculateLaborCost();
    }
  }, [laborLines, throughputUnitsPerShift, showLaborSection]);

  const handleAddLaborLine = () => {
    if (!newLaborLine.employee_type.trim()) {
      toast.error("Employee type is required");
      return;
    }
    const line = {
      id: `labor-${Date.now()}`,
      employee_type: newLaborLine.employee_type,
      employee_count: parseFloat(newLaborLine.employee_count) || 1,
      hourly_rate: parseFloat(newLaborLine.hourly_rate) || laborSettings.default_wage_rates[newLaborLine.employee_type] || 0,
      is_rate_overridden: newLaborLine.hourly_rate > 0
    };
    setLaborLines(prev => [...prev, line]);
    setShowAddLaborLineModal(false);
    setNewLaborLine({ employee_type: '', employee_count: 1, hourly_rate: 0 });
  };

  const handleUpdateLaborLine = (lineId, field, value) => {
    setLaborLines(prev => prev.map(line => {
      if (line.id === lineId) {
        const updated = { ...line, [field]: value };
        if (field === 'hourly_rate') {
          updated.is_rate_overridden = true;
        }
        return updated;
      }
      return line;
    }));
  };

  const handleDeleteLaborLine = (lineId) => {
    setLaborLines(prev => prev.filter(line => line.id !== lineId));
  };

  const handleSaveLabor = async () => {
    try {
      await axios.put(`${API}/rd/items/${itemId}/labor`, {
        lines: laborLines,
        throughput_units_per_shift: throughputUnitsPerShift
      });
      toast.success("Labor configuration saved");
    } catch (error) {
      toast.error("Failed to save labor configuration");
    }
  };

  const handleSaveLaborSettings = async () => {
    try {
      await axios.put(`${API}/rd/settings/labor`, laborSettings);
      toast.success("Labor settings saved");
      setShowLaborSettingsModal(false);
    } catch (error) {
      toast.error("Failed to save labor settings");
    }
  };

  const handleAddDefaultWageRate = (type, rate) => {
    setLaborSettings(prev => ({
      ...prev,
      default_wage_rates: {
        ...prev.default_wage_rates,
        [type]: parseFloat(rate) || 0
      }
    }));
  };

  const handleRemoveDefaultWageRate = (type) => {
    setLaborSettings(prev => {
      const newRates = { ...prev.default_wage_rates };
      delete newRates[type];
      return { ...prev, default_wage_rates: newRates };
    });
  };

  // ============ THROUGHPUT HANDLERS ============
  
  const calculateThroughputFromConfig = (config) => {
    const upm = parseFloat(config.units_per_minute) || 0;
    const efficiency = parseFloat(config.efficiency_rate) || 85;
    const hours = parseFloat(config.hours_per_shift) || 8;
    return Math.round(upm * (efficiency / 100) * 60 * hours);
  };

  const handleUpdateThroughput = async (field, value) => {
    const newConfig = { ...throughputConfig, [field]: value };
    const calculated = calculateThroughputFromConfig(newConfig);
    newConfig.calculated_throughput = calculated;
    setThroughputConfig(newConfig);
    setThroughputUnitsPerShift(calculated);
    
    // Auto-save throughput
    try {
      await axios.put(`${API}/rd/items/${itemId}/throughput`, newConfig);
    } catch (error) {
      console.error('Failed to save throughput:', error);
    }
  };

  // ============ MISCELLANEOUS COSTS HANDLERS ============
  
  const calculateMiscCosts = async () => {
    if (miscCostLines.length === 0) {
      setMiscCostBreakdown(null);
      return;
    }
    try {
      const response = await axios.post(`${API}/rd/items/${itemId}/misc-costs/calculate`, {
        lines: miscCostLines,
        throughput_units_per_shift: throughputUnitsPerShift
      });
      setMiscCostBreakdown(response.data);
    } catch (error) {
      console.error('Failed to calculate misc costs:', error);
    }
  };

  useEffect(() => {
    if (miscCostLines.length > 0 || showMiscCostsSection) {
      calculateMiscCosts();
    }
  }, [miscCostLines, throughputUnitsPerShift, showMiscCostsSection]);

  const handleAddMiscCostLine = () => {
    if (!newMiscCostLine.cost_type.trim()) {
      toast.error("Cost type is required");
      return;
    }
    const line = {
      id: `misc-${Date.now()}`,
      cost_type: newMiscCostLine.cost_type,
      cost_method: newMiscCostLine.cost_method,
      cost_value: parseFloat(newMiscCostLine.cost_value) || 0
    };
    setMiscCostLines(prev => [...prev, line]);
    setShowAddMiscCostModal(false);
    setNewMiscCostLine({ cost_type: '', cost_method: 'per_unit', cost_value: 0 });
  };

  const handleUpdateMiscCostLine = (lineId, field, value) => {
    setMiscCostLines(prev => prev.map(line => 
      line.id === lineId ? { ...line, [field]: field === 'cost_value' ? parseFloat(value) || 0 : value } : line
    ));
  };

  const handleDeleteMiscCostLine = (lineId) => {
    setMiscCostLines(prev => prev.filter(line => line.id !== lineId));
  };

  const handleSaveMiscCosts = async () => {
    try {
      await axios.put(`${API}/rd/items/${itemId}/misc-costs`, {
        lines: miscCostLines
      });
      toast.success("Miscellaneous costs saved");
    } catch (error) {
      toast.error("Failed to save miscellaneous costs");
    }
  };

  // ============ MASTER SAVE FUNCTION ============
  
  const handleSaveAll = async () => {
    setIsSaving(true);
    try {
      await Promise.all([
        // Save formulation
        axios.put(`${API}/rd/formulations/${activeFormulation?.id}`, {
          lines: formulationLines,
          total_cost: calculateTotalCost()
        }),
        // Save packaging
        axios.put(`${API}/rd/items/${itemId}/packaging`, {
          config: packagingConfig,
          lines: packagingLines
        }),
        // Save labor
        axios.put(`${API}/rd/items/${itemId}/labor`, {
          lines: laborLines,
          throughput_units_per_shift: throughputUnitsPerShift
        }),
        // Save throughput
        axios.put(`${API}/rd/items/${itemId}/throughput`, throughputConfig),
        // Save misc costs
        axios.put(`${API}/rd/items/${itemId}/misc-costs`, { lines: miscCostLines }),
        // Save pricing
        axios.put(`${API}/rd/items/${itemId}/pricing`, pricingConfig)
      ]);
      setHasUnsavedChanges(false);
      toast.success("All changes saved successfully");
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error("Failed to save some changes");
    } finally {
      setIsSaving(false);
    }
  };

  // Track changes in key data
  useEffect(() => {
    setHasUnsavedChanges(true);
  }, [formulationLines, packagingConfig, packagingLines, laborLines, throughputConfig, miscCostLines, pricingConfig]);

  // ============ LABOR LINE REORDERING ============
  
  const handleMoveLaborLine = (index, direction) => {
    const newLines = [...laborLines];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newLines.length) return;
    [newLines[index], newLines[targetIndex]] = [newLines[targetIndex], newLines[index]];
    setLaborLines(newLines);
  };

  // ============ PRICING & TOTAL COST ============
  
  const calculateTotalProductCost = async () => {
    try {
      // Use item-level override if set, otherwise fall back to project rate
      const effectiveRate = item?.broker_rate_override !== null && item?.broker_rate_override !== undefined
        ? item.broker_rate_override
        : (project?.broker_rate || 0);
      
      const response = await axios.post(`${API}/rd/items/${itemId}/total-cost/calculate`, {
        formulation_cost: getFormulationCostPerUnit(),
        packaging_cost: packagingCostBreakdown?.total_per_unit || 0,
        labor_cost: laborCostBreakdown?.labor_cost_per_unit || 0,
        misc_cost: miscCostBreakdown?.total_per_unit || 0,
        throughput_units_per_shift: throughputConfig.calculated_throughput || throughputUnitsPerShift || 0,
        pricing_strategy: pricingConfig.pricing_strategy,
        overhead_per_shift: pricingConfig.overhead_per_shift,
        turnkey_price: pricingConfig.turnkey_price,
        brokerage_rate: effectiveRate  // Item override > Project rate
      });
      setTotalCostBreakdown(response.data);
    } catch (error) {
      console.error('Failed to calculate total cost:', error);
    }
  };

  // Helper to calculate formulation cost per unit
  const getFormulationCostPerUnit = () => {
    if (!formulationLines?.length) return 0;
    return formulationLines.reduce((sum, line) => {
      const bomCost = calculateBomCost(line.bom_qty_grams, line.conversion_factor, line.cost_per_uom, line.yield_percent);
      return sum + bomCost;
    }, 0);
  };

  useEffect(() => {
    if (showTotalCostSection && itemId) {
      calculateTotalProductCost();
    }
  }, [packagingCostBreakdown, laborCostBreakdown, miscCostBreakdown, pricingConfig, throughputConfig, showTotalCostSection, itemId, formulationLines, project?.broker_rate, item?.broker_rate_override]);

  const handleUpdatePricing = async (field, value) => {
    const newConfig = { ...pricingConfig, [field]: value };
    setPricingConfig(newConfig);
    
    // Auto-save pricing
    try {
      await axios.put(`${API}/rd/items/${itemId}/pricing`, newConfig);
    } catch (error) {
      console.error('Failed to save pricing:', error);
    }
  };

  const fetchFormulationLines = async (formulationId) => {
    try {
      const response = await axios.get(`${API}/rd/formulations/${formulationId}/lines`);
      setFormulationLines(response.data);
    } catch (error) {
      setFormulationLines(MOCK_FORMULATION_LINES);
    }
  };

  const handleSelectFormulation = (formulation) => {
    // Before switching, save current formulation lines to local state
    if (activeFormulation && formulationLines.length > 0) {
      const currentCost = calculateTotalCost();
      setFormulations(prev => prev.map(f => 
        f.id === activeFormulation.id 
          ? { ...f, lines: formulationLines, total_cost: currentCost }
          : f
      ));
    }
    
    setActiveFormulation(formulation);
    
    // Check if we have cached lines for this formulation
    const cachedForm = formulations.find(f => f.id === formulation.id);
    if (cachedForm?.lines && cachedForm.lines.length > 0) {
      setFormulationLines(cachedForm.lines);
    } else {
      fetchFormulationLines(formulation.id);
    }
  };

  const handleSetActiveFormulation = async (formulation) => {
    try {
      await axios.put(`${API}/rd/formulations/${formulation.id}/set-active`);
    } catch (error) {}
    setFormulations(prev => prev.map(f => ({ ...f, is_active: f.id === formulation.id })));
    setActiveFormulation({ ...formulation, is_active: true });
    toast.success(`${formulation.version} set as active`);
  };

  // Review workflow handlers
  const handleSubmitForReview = async () => {
    try {
      await axios.post(`${API}/rd/items/${itemId}/submit`, null, { params: { user: 'team_member' } });
      toast.success('Item submitted for review');
      setItem(prev => ({ ...prev, review_status: 'submitted' }));
      setShowReviewModal(false);
      fetchActivityLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit for review');
    }
  };

  const handleApproveItem = async () => {
    try {
      await axios.post(`${API}/rd/items/${itemId}/approve`, null, { 
        params: { reviewer: 'reviewer', notes: reviewNotes } 
      });
      toast.success('Item approved');
      setItem(prev => ({ ...prev, review_status: 'approved' }));
      setShowReviewModal(false);
      setReviewNotes('');
      fetchActivityLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve item');
    }
  };

  const handleRejectItem = async () => {
    try {
      await axios.post(`${API}/rd/items/${itemId}/reject`, null, { 
        params: { reviewer: 'reviewer', notes: reviewNotes } 
      });
      toast.success('Item rejected');
      setItem(prev => ({ ...prev, review_status: 'rejected' }));
      setShowReviewModal(false);
      setReviewNotes('');
      fetchActivityLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject item');
    }
  };

  const handleRevertToDraft = async () => {
    try {
      await axios.post(`${API}/rd/items/${itemId}/revert-to-draft`, null, { params: { user: 'team_member' } });
      toast.success('Item reverted to draft');
      setItem(prev => ({ ...prev, review_status: 'draft' }));
      fetchActivityLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revert to draft');
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await axios.post(`${API}/rd/items/${itemId}/export-pdf`, null, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rd_spec_${item?.name?.replace(/\s+/g, '_') || 'item'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF exported successfully');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  // Auto-set conversion factor when UOM changes
  const handleUomChange = (lineId, newUom) => {
    const isFixedUom = FIXED_CONVERSIONS.hasOwnProperty(newUom);
    const conversionFactor = isFixedUom ? FIXED_CONVERSIONS[newUom] : 1;
    
    setFormulationLines(prev => prev.map(line => 
      line.id === lineId ? { ...line, cost_uom: newUom, conversion_factor: conversionFactor } : line
    ));
  };

  // Convert allergens object to array for storage
  const allergensToArray = (allergensObj) => {
    if (Array.isArray(allergensObj)) return allergensObj;
    if (!allergensObj || typeof allergensObj !== 'object') return [];
    return ALLERGEN_CONFIG.filter(a => allergensObj[a.id]).map(a => a.label);
  };

  // Convert allergens array to object for checkboxes
  const allergensToObject = (allergensArr) => {
    const obj = emptyAllergens();
    if (Array.isArray(allergensArr)) {
      allergensArr.forEach(a => {
        const config = ALLERGEN_CONFIG.find(c => c.label.toLowerCase() === a.toLowerCase() || c.id === a.toLowerCase().replace(/\s+/g, '_'));
        if (config) obj[config.id] = true;
      });
    } else if (allergensArr && typeof allergensArr === 'object') {
      return { ...obj, ...allergensArr };
    }
    return obj;
  };

  // File upload handler
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 25MB');
      return;
    }
    
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/rd/items/${itemId}/files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { user: 'user' }
      });
      
      if (res.data.success) {
        setFiles(prev => [res.data.file, ...prev]);
        toast.success('File uploaded successfully');
        fetchActivityLog();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploadingFile(false);
      event.target.value = '';
    }
  };

  // File delete handler
  const handleDeleteFile = async (fileId, filename) => {
    if (!confirm(`Delete ${filename}?`)) return;
    
    try {
      await axios.delete(`${API}/rd/items/${itemId}/files/${fileId}`, {
        params: { user: 'user' }
      });
      setFiles(prev => prev.filter(f => f.id !== fileId));
      toast.success('File deleted');
      fetchActivityLog();
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  // File download handler
  const handleDownloadFile = async (fileId, filename) => {
    try {
      const res = await axios.get(`${API}/rd/items/${itemId}/files/${fileId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  // Summary update handler
  const handleSaveSummary = async () => {
    try {
      await axios.put(`${API}/rd/items/${itemId}/summary`, null, {
        params: { summary: summaryText, user: 'user' }
      });
      setItem(prev => ({ ...prev, summary: summaryText }));
      toast.success('Summary saved');
      fetchActivityLog();
    } catch (error) {
      toast.error('Failed to save summary');
    }
  };

  const handleAddLine = () => {
    const isFixedUom = FIXED_CONVERSIONS.hasOwnProperty(newLine.cost_uom);
    const newLineItem = {
      ...newLine,
      id: `line-${Date.now()}`,
      bom_qty_grams: parseFloat(newLine.bom_qty_grams) || 0,
      yield_percent: parseFloat(newLine.yield_percent) || 100,
      conversion_factor: isFixedUom ? FIXED_CONVERSIONS[newLine.cost_uom] : (parseFloat(newLine.conversion_factor) || 1),
      cost_per_uom: parseFloat(newLine.cost_per_uom) || 0,
      allergens: allergensToArray(newLine.allergens)
    };
    setFormulationLines(prev => [...prev, newLineItem]);
    setShowAddLineModal(false);
    setNewLine({
      item_id: "", item_name: "", item_type: "existing",
      bom_qty_grams: "", yield_percent: 100, cost_uom: "lb",
      conversion_factor: 453.592, cost_per_uom: "", category: "", 
      allergens: emptyAllergens(), ingredient_statement: "", allergens_auto_detected: false
    });
    toast.success("Ingredient added");
  };

  const handleCreatePendingItem = async () => {
    const payload = {
      ...newPendingItem,
      estimated_cost: parseFloat(newPendingItem.estimated_cost) || 0,
      allergens: allergensToArray(newPendingItem.allergens)
    };
    
    try {
      const res = await axios.post(`${API}/rd/pending-items`, payload);
      const pendingItem = res.data;
      const isFixedUom = FIXED_CONVERSIONS.hasOwnProperty(pendingItem.cost_uom);
      
      const newLineItem = {
        id: `line-${Date.now()}`,
        item_id: pendingItem.id,
        item_name: pendingItem.name,
        item_type: "pending",
        bom_qty_grams: 0,
        yield_percent: 100,
        cost_uom: pendingItem.cost_uom,
        conversion_factor: isFixedUom ? FIXED_CONVERSIONS[pendingItem.cost_uom] : 1,
        cost_per_uom: parseFloat(pendingItem.estimated_cost) || 0,
        category: pendingItem.category,
        allergens: pendingItem.allergens || [],
        ingredient_statement: pendingItem.ingredient_statement || "",
        allergens_auto_detected: newPendingItem.allergens_auto_detected
      };
      setFormulationLines(prev => [...prev, newLineItem]);
      toast.success("Pending item created and added");
    } catch (error) {
      const pendingItem = { ...payload, id: `pending-${Date.now()}`, status: "pending" };
      const isFixedUom = FIXED_CONVERSIONS.hasOwnProperty(pendingItem.cost_uom);
      const newLineItem = {
        id: `line-${Date.now()}`,
        item_id: pendingItem.id,
        item_name: pendingItem.name,
        item_type: "pending",
        bom_qty_grams: 0,
        yield_percent: 100,
        cost_uom: pendingItem.cost_uom,
        conversion_factor: isFixedUom ? FIXED_CONVERSIONS[pendingItem.cost_uom] : 1,
        cost_per_uom: parseFloat(pendingItem.estimated_cost) || 0,
        category: pendingItem.category,
        allergens: payload.allergens || [],
        ingredient_statement: newPendingItem.ingredient_statement || "",
        allergens_auto_detected: newPendingItem.allergens_auto_detected
      };
      setFormulationLines(prev => [...prev, newLineItem]);
      toast.success("Pending item created and added");
    }
    setShowCreatePendingModal(false);
    setNewPendingItem({
      name: "", sku: "", description: "", category: "",
      item_type: "raw_material", estimated_cost: "", cost_uom: "lb",
      supplier_name: "", notes: "", allergens: emptyAllergens(), ingredient_statement: "", allergens_auto_detected: false
    });
  };

  const handleCreateNewVersion = () => {
    const nextVersion = `v${formulations.length + 1}`;
    const newFormulation = {
      id: `form-${Date.now()}`,
      version: nextVersion,
      name: newVersion.name || `Version ${formulations.length + 1}`,
      is_active: false,
      created_at: new Date().toISOString().split('T')[0],
      total_cost: calculateTotalCost()
    };
    setFormulations(prev => [...prev, newFormulation]);
    setActiveFormulation(newFormulation);
    setFormulationLines(prev => prev.map(line => ({ ...line, id: `line-${Date.now()}-${Math.random()}` })));
    setShowNewVersionModal(false);
    setNewVersion({ name: "", notes: "" });
    toast.success(`Created ${nextVersion}`);
  };

  const handleUpdateLine = (lineId, field, value) => {
    setFormulationLines(prev => prev.map(line => 
      line.id === lineId ? { ...line, [field]: value } : line
    ));
  };

  const handleSaveFormulation = async () => {
    if (!activeFormulation) return;
    
    try {
      const currentCost = calculateTotalCost();
      const payload = {
        lines: formulationLines,
        total_cost: currentCost
      };
      await axios.put(`${API}/rd/formulations/${activeFormulation.id}`, payload);
      
      // Update local formulations state with saved data
      setFormulations(prev => prev.map(f => 
        f.id === activeFormulation.id 
          ? { ...f, lines: formulationLines, total_cost: currentCost }
          : f
      ));
      
      toast.success("Formulation saved");
    } catch (error) {
      console.error('Failed to save formulation:', error);
      toast.error("Failed to save formulation");
    }
  };

  const handleDeleteLine = (lineId) => {
    setFormulationLines(prev => prev.filter(line => line.id !== lineId));
    toast.success("Ingredient removed");
  };

  const handleSelectExistingItem = (existingItem) => {
    const defaultUom = "lb";
    setNewLine({
      ...newLine,
      item_id: existingItem.id,
      item_name: existingItem.name,
      item_type: "existing",
      category: existingItem.category,
      cost_uom: defaultUom,
      conversion_factor: FIXED_CONVERSIONS[defaultUom]
    });
    setItemSearch("");
  };

  const handleToggleCompare = (formulation) => {
    setCompareFormulations(prev => {
      if (prev.find(f => f.id === formulation.id)) {
        return prev.filter(f => f.id !== formulation.id);
      }
      if (prev.length >= 3) {
        toast.error("Maximum 3 versions can be compared");
        return prev;
      }
      return [...prev, formulation];
    });
  };

  const handleOpenCompare = async () => {
    if (compareFormulations.length < 2) {
      toast.error("Select at least 2 versions to compare");
      return;
    }
    const data = {};
    for (const form of compareFormulations) {
      try {
        const res = await axios.get(`${API}/rd/formulations/${form.id}/lines`);
        data[form.id] = res.data;
      } catch (error) {
        data[form.id] = MOCK_FORMULATION_LINES;
      }
    }
    setCompareData(data);
    setShowCompareModal(true);
  };

  // Click ingredient to view details
  const handleViewIngredient = (line) => {
    setSelectedIngredient(line);
    setShowIngredientDetailModal(true);
  };

  // Auto-detect allergens when ingredient statement changes
  const handleIngredientStatementChange = (setter, currentState, value) => {
    const { detected, hasAutoDetected } = detectAllergens(value);
    
    // Merge auto-detected with existing manual selections
    const mergedAllergens = { ...currentState.allergens };
    Object.keys(detected).forEach(key => {
      if (detected[key]) mergedAllergens[key] = true;
    });
    
    setter({
      ...currentState,
      ingredient_statement: value,
      allergens: mergedAllergens,
      allergens_auto_detected: hasAutoDetected
    });
    
    if (hasAutoDetected) {
      const detectedNames = ALLERGEN_CONFIG.filter(a => detected[a.id]).map(a => a.label).join(", ");
      toast.info(`Auto-detected allergens: ${detectedNames}`, { duration: 3000 });
    }
  };

  // Calculations - BOM Qty → Conversion → divide by Yield % = BOM Cost
  const calculateBomCost = (bomQty, conversionFactor, costPerUom, yieldPercent = 100) => {
    const baseCost = (bomQty / conversionFactor) * costPerUom;
    return baseCost / (yieldPercent / 100);
  };
  
  const calculateTotalCost = () => formulationLines.reduce((sum, line) => {
    return sum + calculateBomCost(line.bom_qty_grams, line.conversion_factor, line.cost_per_uom, line.yield_percent);
  }, 0);

  const totalWeight = formulationLines.reduce((sum, line) => sum + (parseFloat(line.bom_qty_grams) || 0), 0);
  const totalCost = calculateTotalCost();
  const variance = totalCost - (item?.target_cost || 0);

  // Drag and Drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Handle drag end for ingredients
  const handleIngredientDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setFormulationLines((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        setHasUnsavedChanges(true);
        return newItems;
      });
    }
  };

  // Handle drag end for labor lines
  const handleLaborDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setLaborLines((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        setHasUnsavedChanges(true);
        return newItems;
      });
    }
  };

  // Handle drag end for packaging lines
  const handlePackagingDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setPackagingLines((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        setHasUnsavedChanges(true);
        return newItems;
      });
    }
  };

  // Handle drag end for misc cost lines
  const handleMiscCostDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setMiscCostLines((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        setHasUnsavedChanges(true);
        return newItems;
      });
    }
  };

  // Prepare chart data for Version Cost History
  const versionCostChartData = useMemo(() => {
    if (!formulations || formulations.length === 0) return [];
    
    // Sort formulations by version number (v1, v2, v3...)
    const sortedFormulations = [...formulations].sort((a, b) => {
      const versionA = parseInt(a.version?.replace(/\D/g, '') || '0');
      const versionB = parseInt(b.version?.replace(/\D/g, '') || '0');
      return versionA - versionB;
    });
    
    return sortedFormulations.map(form => ({
      version: form.version,
      name: form.name,
      cost: form.total_cost || 0,
      date: form.created_at?.split('T')[0] || '',
      isActive: form.is_active
    }));
  }, [formulations]);

  const filteredExistingItems = existingItems.filter(item => 
    itemSearch === "" || 
    item.name.toLowerCase().includes(itemSearch.toLowerCase()) ||
    item.sku?.toLowerCase().includes(itemSearch.toLowerCase())
  );

  // Get all unique ingredients across compared formulations
  const getCompareIngredients = () => {
    const allIngredients = new Map();
    compareFormulations.forEach(form => {
      const lines = compareData[form.id] || [];
      lines.forEach(line => {
        if (!allIngredients.has(line.item_name)) {
          allIngredients.set(line.item_name, { name: line.item_name, category: line.category });
        }
      });
    });
    return Array.from(allIngredients.values());
  };

  const getLineForIngredient = (formId, ingredientName) => {
    const lines = compareData[formId] || [];
    return lines.find(l => l.item_name === ingredientName);
  };

  // Nutrition Facts Generation
  const generateIngredientStatement = () => {
    const sorted = [...formulationLines].sort((a, b) => b.bom_qty_grams - a.bom_qty_grams);
    const formatted = sorted.map(l => {
      if (l.ingredient_statement && l.ingredient_statement.trim()) {
        return `${l.item_name} (${l.ingredient_statement.trim()})`;
      }
      return l.item_name;
    });
    return formatted.join(", ") + ".";
  };

  const generateAllergenStatement = () => {
    const allAllergens = new Set();
    formulationLines.forEach(line => {
      const allergenArr = Array.isArray(line.allergens) ? line.allergens : allergensToArray(line.allergens);
      allergenArr.forEach(a => {
        allAllergens.add(a);
      });
    });
    if (allAllergens.size === 0) return null;
    return `Contains: ${Array.from(allAllergens).join(", ")}.`;
  };

  const handleOpenNutrition = async () => {
    const newServingSize = { grams: totalWeight, description: `1 ${item?.name || 'serving'}` };
    setServingSize(newServingSize);
    setShowNutritionModal(true);
    
    // Fetch calculated nutrition data
    setNutritionLoading(true);
    try {
      const payload = {
        serving_size_grams: newServingSize.grams,
        ingredients: formulationLines.map(l => ({
          name: l.item_name,
          grams: l.bom_qty_grams
        }))
      };
      const response = await axios.post(`${API}/rd/nutrition/calculate-formulation`, payload);
      setNutritionData(response.data);
    } catch (error) {
      console.error('Failed to calculate nutrition:', error);
      setNutritionData(null);
    } finally {
      setNutritionLoading(false);
    }
  };

  // Recalculate nutrition when serving size changes
  const handleServingSizeChange = async (newGrams) => {
    setServingSize(prev => ({ ...prev, grams: newGrams }));
    
    setNutritionLoading(true);
    try {
      const payload = {
        serving_size_grams: newGrams,
        ingredients: formulationLines.map(l => ({
          name: l.item_name,
          grams: l.bom_qty_grams
        }))
      };
      const response = await axios.post(`${API}/rd/nutrition/calculate-formulation`, payload);
      setNutritionData(response.data);
    } catch (error) {
      console.error('Failed to recalculate nutrition:', error);
    } finally {
      setNutritionLoading(false);
    }
  };

  // USDA Quick Import Functions
  const handleUsdaQuickSearch = async (ingredientName) => {
    setUsdaSearchIngredient(ingredientName);
    setUsdaQuickLoading(true);
    setUsdaQuickResults([]);
    
    try {
      const response = await axios.get(`${API}/rd/nutrition/usda/search?query=${encodeURIComponent(ingredientName)}`);
      setUsdaQuickResults(response.data.foods?.slice(0, 5) || []);
    } catch (error) {
      console.error('USDA search failed:', error);
      toast.error("Failed to search USDA database");
    } finally {
      setUsdaQuickLoading(false);
    }
  };

  const handleUsdaQuickImport = async (fdcId, description) => {
    setUsdaImporting(fdcId);
    try {
      await axios.post(`${API}/rd/nutrition/usda/import`, { fdc_id: fdcId });
      toast.success(`Imported: ${description}`);
      
      // Refresh nutrition calculation
      const payload = {
        serving_size_grams: servingSize.grams,
        ingredients: formulationLines.map(l => ({
          name: l.item_name,
          grams: l.bom_qty_grams
        }))
      };
      const response = await axios.post(`${API}/rd/nutrition/calculate-formulation`, payload);
      setNutritionData(response.data);
      
      // Clear search
      setUsdaSearchIngredient(null);
      setUsdaQuickResults([]);
    } catch (error) {
      console.error('USDA import failed:', error);
      toast.error("Failed to import from USDA");
    } finally {
      setUsdaImporting(null);
    }
  };

  const handleGeneratePDF = async () => {
    try {
      const payload = {
        item_name: item?.name,
        serving_size_grams: servingSize.grams,
        serving_size_description: servingSize.description,
        servings_per_container: 1,
        ingredients: formulationLines.map(l => ({
          name: l.item_name,
          grams: l.bom_qty_grams,
          allergens: Array.isArray(l.allergens) ? l.allergens : allergensToArray(l.allergens),
          ingredient_statement: l.ingredient_statement || ""
        })),
        format: selectedLabelFormat
      };
      
      const response = await axios.post(`${API}/rd/nutrition/generate-label`, payload, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `nutrition_label_${item?.name?.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF generated!");
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error("Failed to generate PDF. Using preview mode.");
    }
  };

  // Inline component for nutrition lookup
  const NutritionLookup = ({ ingredientName }) => {
    const [nutritionInfo, setNutritionInfo] = useState(null);
    const [lookupLoading, setLookupLoading] = useState(true);
    
    useEffect(() => {
      const fetchNutrition = async () => {
        try {
          const response = await axios.get(`${API}/rd/nutrition`);
          const allNutrition = response.data;
          const nameLower = ingredientName.toLowerCase();
          
          // Find matching nutrition entry
          let match = allNutrition.find(n => n.name.toLowerCase() === nameLower);
          if (!match) {
            match = allNutrition.find(n => 
              n.name.toLowerCase().includes(nameLower) || 
              nameLower.includes(n.name.toLowerCase())
            );
          }
          setNutritionInfo(match);
        } catch (error) {
          console.error('Failed to fetch nutrition:', error);
        } finally {
          setLookupLoading(false);
        }
      };
      fetchNutrition();
    }, [ingredientName]);
    
    if (lookupLoading) {
      return <div className="text-sm text-gray-400">Loading nutrition data...</div>;
    }
    
    if (!nutritionInfo) {
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-2 text-sm">
          <p className="text-yellow-800">No nutrition data found for this ingredient.</p>
          <p className="text-yellow-600 text-xs mt-1">Add it to the Nutrition Database to see values here.</p>
        </div>
      );
    }
    
    return (
      <div className="bg-gray-50 rounded p-3 mt-2">
        <div className="flex items-center justify-between mb-2">
          <span className="font-medium text-sm">{nutritionInfo.name}</span>
          <Badge variant="outline" className="text-xs">{nutritionInfo.source}</Badge>
        </div>
        <div className="grid grid-cols-4 gap-2 text-xs">
          <div className="text-center p-2 bg-white rounded border">
            <div className="font-bold">{nutritionInfo.calories}</div>
            <div className="text-gray-500">Cal</div>
          </div>
          <div className="text-center p-2 bg-white rounded border">
            <div className="font-bold">{nutritionInfo.protein}g</div>
            <div className="text-gray-500">Protein</div>
          </div>
          <div className="text-center p-2 bg-white rounded border">
            <div className="font-bold">{nutritionInfo.total_carbs}g</div>
            <div className="text-gray-500">Carbs</div>
          </div>
          <div className="text-center p-2 bg-white rounded border">
            <div className="font-bold">{nutritionInfo.total_fat}g</div>
            <div className="text-gray-500">Fat</div>
          </div>
        </div>
        {nutritionInfo.ingredient_statement && (
          <div className="mt-2 text-xs text-gray-600">
            <span className="font-medium">Ingredients: </span>
            {nutritionInfo.ingredient_statement.substring(0, 100)}
            {nutritionInfo.ingredient_statement.length > 100 && '...'}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="p-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-1" />Back
        </Button>
        <div className="text-center py-12 text-gray-500">Item not found</div>
      </div>
    );
  }

  return (
    <>
      {/* Fixed Header - stays at top while scrolling (below app header) */}
      <div 
        className="fixed right-0 z-30 bg-white border-b shadow-sm px-4 py-3"
        style={{ top: '56px', left: `${sidebarWidth}px` }}
        data-testid="sticky-header"
      >
        <div className="flex items-center justify-between max-w-[1800px] mx-auto">
          {/* Left: Back + Item Name + Status */}
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 w-8 p-0" 
              onClick={() => navigate(`/rd/projects/${item.project_id}`)}
              data-testid="back-to-project-btn"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-3">
              <h1 className="text-lg font-bold text-gray-900" data-testid="item-name">
                {item.name}
              </h1>
              <Badge 
                className={`text-xs ${
                  item.review_status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                  item.review_status === 'submitted' ? 'bg-amber-100 text-amber-700' :
                  item.review_status === 'rejected' ? 'bg-red-100 text-red-700' :
                  'bg-slate-100 text-slate-600'
                }`}
                data-testid="review-status-badge"
              >
                {item.review_status === 'approved' && <CheckCircle className="w-3 h-3 mr-1 inline" />}
                {item.review_status === 'submitted' && <Clock className="w-3 h-3 mr-1 inline" />}
                {item.review_status === 'rejected' && <XCircle className="w-3 h-3 mr-1 inline" />}
                {(item.review_status || 'draft').charAt(0).toUpperCase() + (item.review_status || 'draft').slice(1)}
              </Badge>
            </div>
          </div>

          {/* Right: Actions Dropdown + Save Button */}
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8" data-testid="actions-dropdown-trigger">
                  <MoreHorizontal className="w-4 h-4 mr-1" />
                  Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={handleExportPDF} data-testid="action-export-pdf">
                  <Download className="w-4 h-4 mr-2" />
                  Export PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleOpenNutrition} data-testid="action-nutrition-label">
                  <FileText className="w-4 h-4 mr-2" />
                  Nutrition Label
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowNewVersionModal(true)} data-testid="action-new-version">
                  <Plus className="w-4 h-4 mr-2" />
                  New Version
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {/* Review Actions */}
                {(!item.review_status || item.review_status === 'draft') && (
                  <DropdownMenuItem 
                    onClick={handleSubmitForReview} 
                    className="text-amber-600"
                    data-testid="action-submit-review"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Submit for Review
                  </DropdownMenuItem>
                )}
                {item.review_status === 'submitted' && (
                  <>
                    <DropdownMenuItem 
                      onClick={() => { setReviewAction('approve'); setShowReviewModal(true); }}
                      className="text-green-600"
                      data-testid="action-approve"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => { setReviewAction('reject'); setShowReviewModal(true); }}
                      className="text-red-600"
                      data-testid="action-reject"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </DropdownMenuItem>
                  </>
                )}
                {(item.review_status === 'rejected' || item.review_status === 'approved') && (
                  <DropdownMenuItem onClick={handleRevertToDraft} data-testid="action-revert-draft">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Revert to Draft
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button 
              size="sm" 
              className={`transition-all duration-300 ${hasUnsavedChanges 
                ? 'h-9 px-5 bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-500/40 ring-2 ring-amber-300 ring-offset-1 font-semibold' 
                : 'h-8 px-4 bg-green-600 hover:bg-green-700 text-white'}`}
              onClick={handleSaveAll}
              disabled={isSaving}
              data-testid="save-changes-btn"
            >
              <Save className={`mr-1 ${hasUnsavedChanges ? 'w-4 h-4' : 'w-4 h-4'}`} />
              {isSaving ? 'Saving...' : hasUnsavedChanges ? '● Save Changes' : 'Saved'}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Page Content - with top padding for both headers (app header 56px + custom 60px) */}
      <div className="p-4 pt-20 bg-gray-50 min-h-screen">
        {/* Summary Section - Redesigned */}
        <div className="bg-white rounded-lg shadow-sm border mb-4 p-4" data-testid="summary-section">
          <div className="grid grid-cols-12 gap-6">
            {/* Left: Customer, Project & Related Items */}
            <div className="col-span-4 space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <Building2 className="w-4 h-4 text-gray-400" />
                <span className="text-gray-500">Customer:</span>
                <Select 
                  value={project?.customer_id || "none"} 
                  onValueChange={(val) => handleCustomerChange(val === "none" ? null : val)}
                >
                  <SelectTrigger 
                    className="h-7 w-48 text-xs"
                    data-testid="customer-dropdown"
                  >
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <span className="text-gray-400">Not assigned</span>
                    </SelectItem>
                    {customers
                      .filter((c) => c.id != null && String(c.id) !== "")
                      .map((customer) => (
                        <SelectItem key={customer.id} value={String(customer.id)}>
                          <div className="flex items-center gap-2">
                            <span>{customer.name}</span>
                            <span className="text-[10px] text-gray-400">({customer.industry})</span>
                          </div>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <FlaskConical className="w-4 h-4 text-gray-400" />
                <span className="text-gray-500">Project:</span>
                <span 
                  className="font-medium hover:text-indigo-600 cursor-pointer" 
                  onClick={() => navigate(`/rd/projects/${item.project_id}`)}
                >
                  {item.project_name}
                </span>
              </div>
              {/* Related Items Dropdown */}
              {relatedItems.length > 1 && (
                <div className="flex items-center gap-2 text-sm">
                  <Package className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-500">Related Items:</span>
                  <Select 
                    value={item.id} 
                    onValueChange={(val) => navigate(`/rd/items/${val}`)}
                  >
                    <SelectTrigger 
                      className="h-7 w-48 text-xs"
                      data-testid="related-items-dropdown"
                    >
                      <SelectValue placeholder="Select item" />
                    </SelectTrigger>
                    <SelectContent>
                      {relatedItems
                      .filter((r) => r.id != null && String(r.id) !== "")
                      .map((relItem, idx) => (
                        <SelectItem key={relItem.id} value={String(relItem.id)}>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-[10px]">{idx + 1}.</span>
                            <span className={relItem.id === item.id ? 'font-medium' : ''}>
                              {relItem.name}
                            </span>
                            {relItem.id === item.id && (
                              <Badge variant="outline" className="text-[9px] ml-1">Current</Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {/* Broker & Brokerage Rate - Broker flows from Customer, rate can be overridden at ITEM level */}
              <div className="flex items-center gap-2 text-sm flex-wrap">
                <Percent className="w-4 h-4 text-gray-400" />
                <span className="text-gray-500">Broker:</span>
                {project?.broker_name ? (
                  <>
                    <span className="font-medium text-gray-700">{project.broker_name}</span>
                    <span className="text-gray-400">@</span>
                    <div className="relative">
                      <Input
                        type="number"
                        step="0.5"
                        min="0"
                        max="20"
                        value={getEffectiveBrokerRate()}
                        onChange={(e) => handleItemBrokerRateChange(parseFloat(e.target.value) || 0)}
                        className={`h-7 w-16 text-xs text-right ${
                          hasItemRateOverride() 
                            ? 'border-purple-400 bg-purple-50 ring-1 ring-purple-300' 
                            : isProjectRateNonStandard()
                            ? 'border-amber-400 bg-amber-50 ring-1 ring-amber-300'
                            : ''
                        }`}
                        data-testid="broker-rate-input"
                      />
                      {(hasItemRateOverride() || isProjectRateNonStandard()) && (
                        <span className={`absolute -top-1 -right-1 w-2 h-2 rounded-full ${
                          hasItemRateOverride() ? 'bg-purple-500' : 'bg-amber-500'
                        }`} title={hasItemRateOverride() ? 'Item-level override' : 'Project-level override'} />
                      )}
                    </div>
                    <span className="text-gray-500 text-xs">%</span>
                    {hasItemRateOverride() ? (
                      <Badge 
                        variant="outline" 
                        className="text-[9px] text-purple-600 border-purple-300 bg-purple-50 cursor-pointer hover:bg-purple-100"
                        onClick={resetToProjectRate}
                        title="Click to reset to project rate"
                      >
                        Item override (proj: {project.broker_rate}%) ✕
                      </Badge>
                    ) : isProjectRateNonStandard() ? (
                      <Badge variant="outline" className="text-[9px] text-amber-600 border-amber-300 bg-amber-50">
                        Project (std: {getDefaultBrokerRate()}%)
                      </Badge>
                    ) : null}
                  </>
                ) : (
                  <span className="text-gray-400 italic text-xs">
                    {project?.customer_id ? 'Customer has no broker' : 'Select customer first'}
                  </span>
                )}
              </div>
            </div>
            
            {/* Center & Right: Summary Text - now takes more space */}
            <div className="col-span-8">
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs text-gray-500">Summary</Label>
                {summaryText !== (item.summary || '') && (
                  <Button size="sm" variant="ghost" className="h-5 text-[10px] px-2" onClick={handleSaveSummary}>
                    Save
                  </Button>
                )}
              </div>
              <Textarea 
                value={summaryText}
                onChange={(e) => setSummaryText(e.target.value)}
                placeholder="Add a description or notes about this item..."
                className="text-sm h-20 resize-none"
                data-testid="summary-textarea"
              />
            </div>
          </div>
        </div>

      {/* Main Content: 2/3 + 1/3 Split Layout */}
      <div className="flex gap-4">
        {/* Left Column - 2/3 Width */}
        <div className="w-2/3 space-y-4">

          {/* Versions Bar - Compact Horizontal Tabs */}
          <div className="bg-white rounded-lg shadow-sm border px-3 py-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <FlaskConical className="w-4 h-4 text-purple-600" />
                  <span className="font-medium text-sm text-gray-700">Version:</span>
                </div>
                <div className="flex items-center border rounded-lg overflow-hidden">
                  {formulations.map((form, idx) => {
                    const isActive = activeFormulation?.id === form.id;
                    const displayCost = isActive ? calculateTotalCost() : form.total_cost;
                    return (
                      <button 
                        key={form.id}
                        className={`px-3 py-1.5 text-xs font-medium transition-all flex items-center gap-1.5 ${
                          isActive 
                            ? 'bg-indigo-600 text-white' 
                            : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                        } ${idx > 0 ? 'border-l' : ''}`}
                        onClick={() => handleSelectFormulation(form)}
                      >
                        <input 
                          type="checkbox"
                          checked={compareFormulations.some(f => f.id === form.id)}
                          onChange={() => handleToggleCompare(form)}
                          onClick={(e) => e.stopPropagation()}
                      className={`h-3 w-3 rounded border-gray-300 ${isActive ? 'accent-white' : 'text-indigo-600'} focus:ring-indigo-500`}
                    />
                    <span>{form.version}</span>
                    {form.is_active && <span className="text-[9px] px-1 py-0 rounded bg-green-500 text-white">●</span>}
                    <span className={`${isActive ? 'text-indigo-200' : 'text-gray-400'}`}>${displayCost?.toFixed(2)}</span>
                  </button>
                );
              })}
            </div>
            {!formulations.find(f => f.id === activeFormulation?.id)?.is_active && activeFormulation && (
              <Button 
                size="sm" 
                variant="outline" 
                className="h-7 text-xs border-green-300 text-green-600 hover:bg-green-50"
                onClick={() => handleSetActiveFormulation(activeFormulation)}
              >
                <Check className="w-3 h-3 mr-1" />Set Active
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {compareFormulations.length >= 2 && (
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handleOpenCompare}>
                <GitCompare className="w-3 h-3 mr-1" />Compare ({compareFormulations.length})
              </Button>
            )}
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowNewVersionModal(true)}>
              <Plus className="w-3 h-3 mr-1" />New Version
            </Button>
          </div>
        </div>
      </div>

        {/* Formulation Section */}
        <Card className="shadow-sm border-l-4 border-l-amber-500">
          <CardHeader className="py-3 px-4 flex flex-row items-center justify-between bg-gradient-to-r from-amber-50 to-white">
            <div className="flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-amber-600" />
              <CardTitle className="text-base font-semibold">
                {activeFormulation?.version} - {activeFormulation?.name || 'Formulation'}
              </CardTitle>
              <Badge className="bg-amber-100 text-amber-700">
                ${calculateTotalCost().toFixed(2)}
              </Badge>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowCreatePendingModal(true)}>
                <Plus className="w-3 h-3 mr-1" />New Item
              </Button>
              <Button size="sm" className="h-7 text-xs" onClick={() => setShowAddLineModal(true)}>
                <Plus className="w-3 h-3 mr-1" />Add Line
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="w-6"></th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Ingredient</th>
                    <th className="text-left px-2 py-2 font-medium text-gray-600 w-16">Cat.</th>
                    <th className="text-right px-2 py-2 font-medium text-gray-600 w-20">BOM (g)</th>
                    <th className="text-center px-2 py-2 font-medium text-gray-600 w-14">UOM</th>
                    <th className="text-right px-2 py-2 font-medium text-gray-600 w-16">Conv.</th>
                    <th className="text-right px-2 py-2 font-medium text-gray-600 w-16">$/UOM</th>
                    <th className="text-right px-2 py-2 font-medium text-gray-600 w-16">Yield%</th>
                    <th className="text-right px-2 py-2 font-medium text-gray-600 w-16">Cost</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleIngredientDragEnd}>
                  <SortableContext items={formulationLines.map(l => l.id)} strategy={verticalListSortingStrategy}>
                    <tbody className="divide-y">
                      {formulationLines.map(line => {
                        const bomCost = calculateBomCost(line.bom_qty_grams, line.conversion_factor, line.cost_per_uom, line.yield_percent);
                        const isFixedUom = FIXED_CONVERSIONS.hasOwnProperty(line.cost_uom);
                        const lineAllergens = Array.isArray(line.allergens) ? line.allergens : [];
                        
                        return (
                          <SortableTableRow key={line.id} id={line.id} className="hover:bg-gray-50">
                            <td className="px-2 py-1">
                              <div 
                                className="flex items-center gap-1 cursor-pointer hover:text-indigo-600"
                                onClick={() => handleViewIngredient(line)}
                              >
                                <Eye className="w-3 h-3 text-gray-400" />
                                <span className="font-medium">{line.item_name}</span>
                                {line.item_type === 'pending' && (
                                  <Badge className="bg-yellow-100 text-yellow-700 text-[10px] px-1 py-0">P</Badge>
                                )}
                              </div>
                              {lineAllergens.length > 0 && (
                                <div className="flex flex-wrap gap-0.5 mt-0.5">
                                  {lineAllergens.slice(0, 3).map(a => (
                                    <Badge key={a} className="bg-orange-100 text-orange-700 text-[9px] px-1 py-0">{a}</Badge>
                                  ))}
                                  {lineAllergens.length > 3 && (
                                    <Badge className="bg-gray-100 text-gray-600 text-[9px] px-1 py-0">+{lineAllergens.length - 3}</Badge>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="px-2 py-1 text-gray-500">{line.category}</td>
                            <td className="px-2 py-1">
                              <Input
                                type="number"
                                value={line.bom_qty_grams}
                              onChange={(e) => handleUpdateLine(line.id, 'bom_qty_grams', parseFloat(e.target.value) || 0)}
                              className="h-6 text-right text-xs w-16 px-1"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Select 
                              value={line.cost_uom} 
                              onValueChange={(val) => handleUomChange(line.id, val)}
                            >
                              <SelectTrigger className="h-6 text-xs w-14 px-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {COST_UOMS.map(uom => (
                                  <SelectItem key={uom} value={uom}>{uom}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-2 py-1">
                            {isFixedUom ? (
                              <span className="text-gray-400 text-xs">{line.conversion_factor.toFixed(2)}</span>
                            ) : (
                              <Input
                                type="number"
                                value={line.conversion_factor}
                                onChange={(e) => handleUpdateLine(line.id, 'conversion_factor', parseFloat(e.target.value) || 1)}
                                className="h-6 text-right text-xs w-14 px-1"
                              />
                            )}
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              type="number"
                              step="0.01"
                              value={line.cost_per_uom}
                              onChange={(e) => handleUpdateLine(line.id, 'cost_per_uom', parseFloat(e.target.value) || 0)}
                              className="h-6 text-right text-xs w-14 px-1"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              type="number"
                              step="0.1"
                              value={line.yield_percent}
                              onChange={(e) => handleUpdateLine(line.id, 'yield_percent', parseFloat(e.target.value) || 100)}
                              className="h-6 text-right text-xs w-14 px-1"
                            />
                          </td>
                          <td className="px-2 py-1 text-right font-medium text-green-600">${bomCost.toFixed(3)}</td>
                          <td className="px-2 py-1">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                              onClick={() => handleDeleteLine(line.id)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </td>
                        </SortableTableRow>
                      );
                    })}
                    </tbody>
                  </SortableContext>
                </DndContext>
                  <tfoot className="bg-gray-100 border-t">
                    <tr className="font-semibold text-xs">
                      <td></td>
                      <td className="px-2 py-1.5">Total</td>
                      <td></td>
                      <td className="px-2 py-1.5 text-right">{totalWeight.toFixed(0)}g</td>
                      <td colSpan="4"></td>
                      <td className="px-2 py-1.5 text-right text-green-600">${totalCost.toFixed(2)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Packaging Cost Matrix Card */}
          <Card className="shadow-sm border-l-4 border-l-purple-500">
            <CardHeader className="py-3 px-4 cursor-pointer bg-gradient-to-r from-purple-50 to-white" onClick={() => setShowPackagingSection(!showPackagingSection)}>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Package className="w-5 h-5 text-purple-600" />
                  Packaging Cost Matrix
                  {packagingCostBreakdown && (
                    <Badge className="bg-purple-100 text-purple-700 ml-2">
                      ${packagingCostBreakdown.total_per_unit?.toFixed(4)}/unit
                    </Badge>
                  )}
                </CardTitle>
                <div className="flex items-center gap-1">
                  {showPackagingSection && (
                    <>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); setShowLoadTemplateModal(true); }} title="Load Template">
                        <FileDown className="w-3 h-3 mr-1" />Load
                      </Button>
                      {packagingLines.length > 0 && (
                        <>
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); setShowSaveTemplateModal(true); }} title="Save as Template">
                            <FileUp className="w-3 h-3 mr-1" />Template
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); handleOpenCopyPackagingModal(); }}>
                            <Copy className="w-3 h-3 mr-1" />Copy
                          </Button>
                        </>
                      )}
                    </>
                  )}
                  <Badge variant="outline" className="text-xs ml-1">
                    {showPackagingSection ? '▼' : '▶'}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            
            {showPackagingSection && (
              <CardContent className="pt-0">
                {/* Packaging Hierarchy Config */}
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Layers className="w-4 h-4 text-gray-500" />
                    <span className="text-xs font-medium text-gray-600">Packaging Hierarchy</span>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={packagingConfig.has_carton_level}
                        onChange={(e) => setPackagingConfig({...packagingConfig, has_carton_level: e.target.checked})}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <span className="text-xs">Include Carton Level</span>
                    </label>
                  </div>
                  
                  <div className="flex items-center gap-2 text-xs">
                    <div className="flex items-center gap-1 px-2 py-1 bg-white rounded border">
                      <Box className="w-3 h-3 text-blue-500" />
                      <span>Unit</span>
                    </div>
                    <ArrowRight className="w-3 h-3 text-gray-400" />
                    
                    {packagingConfig.has_carton_level && (
                      <>
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            value={packagingConfig.units_per_carton}
                            onChange={(e) => setPackagingConfig({...packagingConfig, units_per_carton: parseInt(e.target.value) || 1})}
                            className="w-12 h-6 text-xs text-center px-1"
                          />
                          <span className="text-gray-500">per</span>
                        </div>
                        <div className="flex items-center gap-1 px-2 py-1 bg-white rounded border">
                          <Package className="w-3 h-3 text-orange-500" />
                          <span>Carton</span>
                        </div>
                        <ArrowRight className="w-3 h-3 text-gray-400" />
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            value={packagingConfig.cartons_per_case}
                            onChange={(e) => setPackagingConfig({...packagingConfig, cartons_per_case: parseInt(e.target.value) || 1})}
                            className="w-12 h-6 text-xs text-center px-1"
                          />
                          <span className="text-gray-500">per</span>
                        </div>
                      </>
                    )}
                    
                    {!packagingConfig.has_carton_level && (
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          value={packagingConfig.units_per_case}
                          onChange={(e) => setPackagingConfig({...packagingConfig, units_per_case: parseInt(e.target.value) || 1})}
                          className="w-12 h-6 text-xs text-center px-1"
                        />
                        <span className="text-gray-500">per</span>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-1 px-2 py-1 bg-white rounded border">
                      <Package className="w-3 h-3 text-green-500" />
                      <span>Case</span>
                    </div>
                    <ArrowRight className="w-3 h-3 text-gray-400" />
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        value={packagingConfig.cases_per_pallet}
                        onChange={(e) => setPackagingConfig({...packagingConfig, cases_per_pallet: parseInt(e.target.value) || 1})}
                        className="w-12 h-6 text-xs text-center px-1"
                      />
                      <span className="text-gray-500">per</span>
                    </div>
                    <div className="flex items-center gap-1 px-2 py-1 bg-white rounded border">
                      <Layers className="w-3 h-3 text-purple-500" />
                      <span>Pallet</span>
                    </div>
                  </div>
                  
                  {packagingCostBreakdown && (
                    <div className="mt-3 pt-3 border-t text-xs text-gray-500">
                      <span className="font-medium">Summary: </span>
                      {packagingCostBreakdown.config_summary.units_per_case} units/case • 
                      {packagingCostBreakdown.config_summary.cases_per_pallet} cases/pallet • 
                      {packagingCostBreakdown.config_summary.units_per_pallet} units/pallet
                    </div>
                  )}
                </div>
                
                {/* Packaging Components Table - Multi-Column Layout */}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-600">Packaging Components</span>
                  <Button size="sm" variant="outline" className="h-6 text-xs" onClick={handleOpenAddPackagingModal}>
                    <Plus className="w-3 h-3 mr-1" />Add Component
                  </Button>
                </div>
                
                <div className="border rounded overflow-x-auto">
                  <table className="w-full text-xs" data-testid="packaging-cost-table">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left px-2 py-1.5 font-medium text-gray-600 min-w-[140px]">Component</th>
                        <th className="text-center px-2 py-1.5 font-medium text-gray-600 w-14">Qty</th>
                        <th className="text-center px-2 py-1.5 font-medium text-gray-600 w-16">Yield %</th>
                        <th className="text-right px-2 py-1.5 font-medium text-blue-600 bg-blue-50 w-20">
                          <div className="flex items-center justify-end gap-1">
                            <Box className="w-3 h-3" />Unit
                          </div>
                        </th>
                        {packagingConfig.has_carton_level && (
                          <th className="text-right px-2 py-1.5 font-medium text-orange-600 bg-orange-50 w-20">
                            <div className="flex items-center justify-end gap-1">
                              <Package className="w-3 h-3" />Carton
                            </div>
                          </th>
                        )}
                        <th className="text-right px-2 py-1.5 font-medium text-green-600 bg-green-50 w-20">
                          <div className="flex items-center justify-end gap-1">
                            <Package className="w-3 h-3" />Case
                          </div>
                        </th>
                        <th className="text-right px-2 py-1.5 font-medium text-purple-600 bg-purple-50 w-20">
                          <div className="flex items-center justify-end gap-1">
                            <Layers className="w-3 h-3" />Pallet
                          </div>
                        </th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {packagingLines.length === 0 ? (
                        <tr>
                          <td colSpan={packagingConfig.has_carton_level ? 8 : 7} className="px-2 py-4 text-center text-gray-400">
                            No packaging components added. Click "Add Component" to start.
                          </td>
                        </tr>
                      ) : (
                        packagingLines.map(line => {
                          const breakdown = packagingCostBreakdown?.breakdown?.find(b => b.line_id === line.id);
                          const levelColors = {
                            unit: 'bg-blue-100 text-blue-700',
                            carton: 'bg-orange-100 text-orange-700',
                            case: 'bg-green-100 text-green-700',
                            pallet: 'bg-purple-100 text-purple-700'
                          };
                          const levelBgColors = {
                            unit: 'bg-blue-50',
                            carton: 'bg-orange-50',
                            case: 'bg-green-50',
                            pallet: 'bg-purple-50'
                          };
                          
                          return (
                            <tr key={line.id} className="hover:bg-gray-50" data-testid={`packaging-line-${line.id}`}>
                              <td className="px-2 py-1">
                                <div className="flex items-center gap-2">
                                  <Badge className={`text-[9px] px-1 ${levelColors[line.level] || 'bg-gray-100'}`}>
                                    {line.level}
                                  </Badge>
                                  <span className="font-medium truncate">{line.component_name}</span>
                                </div>
                              </td>
                              <td className="px-1 py-1">
                                <Input
                                  type="number"
                                  value={line.quantity}
                                  onChange={(e) => handleUpdatePackagingLine(line.id, 'quantity', parseInt(e.target.value) || 1)}
                                  className="h-6 text-center text-xs w-12 px-1"
                                  data-testid={`packaging-qty-${line.id}`}
                                />
                              </td>
                              <td className="px-1 py-1">
                                <Input
                                  type="number"
                                  step="0.1"
                                  min="1"
                                  max="100"
                                  value={line.yield_percent || 100}
                                  onChange={(e) => handleUpdatePackagingLine(line.id, 'yield_percent', parseFloat(e.target.value) || 100)}
                                  className="h-6 text-center text-xs w-14 px-1"
                                  data-testid={`packaging-yield-${line.id}`}
                                />
                              </td>
                              {/* Unit cost column */}
                              <td className={`px-1 py-1 ${levelBgColors.unit}`}>
                                {line.level === 'unit' ? (
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={line.cost_at_level}
                                    onChange={(e) => handleUpdatePackagingLine(line.id, 'cost_at_level', parseFloat(e.target.value) || 0)}
                                    className="h-6 text-right text-xs w-16 px-1 border-blue-300 bg-white"
                                    data-testid={`packaging-cost-unit-${line.id}`}
                                  />
                                ) : (
                                  <div className="text-right text-xs text-gray-600 pr-1">
                                    ${breakdown?.costs?.unit?.toFixed(4) || '0.0000'}
                                  </div>
                                )}
                              </td>
                              {/* Carton cost column (conditional) */}
                              {packagingConfig.has_carton_level && (
                                <td className={`px-1 py-1 ${levelBgColors.carton}`}>
                                  {line.level === 'carton' ? (
                                    <Input
                                      type="number"
                                      step="0.01"
                                      value={line.cost_at_level}
                                      onChange={(e) => handleUpdatePackagingLine(line.id, 'cost_at_level', parseFloat(e.target.value) || 0)}
                                      className="h-6 text-right text-xs w-16 px-1 border-orange-300 bg-white"
                                      data-testid={`packaging-cost-carton-${line.id}`}
                                    />
                                  ) : (
                                    <div className="text-right text-xs text-gray-600 pr-1">
                                      ${breakdown?.costs?.carton?.toFixed(4) || '0.0000'}
                                    </div>
                                  )}
                                </td>
                              )}
                              {/* Case cost column */}
                              <td className={`px-1 py-1 ${levelBgColors.case}`}>
                                {line.level === 'case' ? (
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={line.cost_at_level}
                                    onChange={(e) => handleUpdatePackagingLine(line.id, 'cost_at_level', parseFloat(e.target.value) || 0)}
                                    className="h-6 text-right text-xs w-16 px-1 border-green-300 bg-white"
                                    data-testid={`packaging-cost-case-${line.id}`}
                                  />
                                ) : (
                                  <div className="text-right text-xs text-gray-600 pr-1">
                                    ${breakdown?.costs?.case?.toFixed(4) || '0.0000'}
                                  </div>
                                )}
                              </td>
                              {/* Pallet cost column */}
                              <td className={`px-1 py-1 ${levelBgColors.pallet}`}>
                                {line.level === 'pallet' ? (
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={line.cost_at_level}
                                    onChange={(e) => handleUpdatePackagingLine(line.id, 'cost_at_level', parseFloat(e.target.value) || 0)}
                                    className="h-6 text-right text-xs w-16 px-1 border-purple-300 bg-white"
                                    data-testid={`packaging-cost-pallet-${line.id}`}
                                  />
                                ) : (
                                  <div className="text-right text-xs text-gray-600 pr-1">
                                    ${breakdown?.costs?.pallet?.toFixed(4) || '0.0000'}
                                  </div>
                                )}
                              </td>
                              <td className="px-1 py-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                                  onClick={() => handleDeletePackagingLine(line.id)}
                                  data-testid={`packaging-delete-${line.id}`}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                    {packagingLines.length > 0 && packagingCostBreakdown?.totals && (
                      <tfoot className="border-t font-semibold text-xs">
                        <tr>
                          <td className="px-2 py-1.5 text-gray-700">Totals</td>
                          <td></td>
                          <td></td>
                          <td className="px-2 py-1.5 text-right text-blue-700 bg-blue-50">
                            ${packagingCostBreakdown.totals.unit?.toFixed(4) || '0.0000'}
                          </td>
                          {packagingConfig.has_carton_level && (
                            <td className="px-2 py-1.5 text-right text-orange-700 bg-orange-50">
                              ${packagingCostBreakdown.totals.carton?.toFixed(4) || '0.0000'}
                            </td>
                          )}
                          <td className="px-2 py-1.5 text-right text-green-700 bg-green-50">
                            ${packagingCostBreakdown.totals.case?.toFixed(4) || '0.0000'}
                          </td>
                          <td className="px-2 py-1.5 text-right text-purple-700 bg-purple-50">
                            ${packagingCostBreakdown.totals.pallet?.toFixed(4) || '0.0000'}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Throughput Calculation Card */}
          <Card className="shadow-sm border-l-4 border-l-cyan-500">
            <CardHeader className="py-3 px-4 cursor-pointer bg-gradient-to-r from-cyan-50 to-white" onClick={() => setShowThroughputSection(!showThroughputSection)}>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-cyan-600" />
                  Throughput Calculation
                  <Badge className="bg-cyan-100 text-cyan-700 ml-2">
                    {(throughputConfig.calculated_throughput || throughputUnitsPerShift || 0).toLocaleString()} units/shift
                  </Badge>
                </CardTitle>
                <Badge variant="outline" className="text-xs">
                  {showThroughputSection ? '▼' : '▶'}
                </Badge>
              </div>
            </CardHeader>
            
            {showThroughputSection && (
              <CardContent className="pt-0">
                <div className="bg-cyan-50 rounded-lg p-4">
                  <div className="grid grid-cols-4 gap-4 mb-3">
                    <div>
                      <Label className="text-xs text-gray-600">Units/Minute</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={throughputConfig.units_per_minute}
                        onChange={(e) => handleUpdateThroughput('units_per_minute', parseFloat(e.target.value) || 0)}
                        className="h-8 text-sm mt-1"
                        placeholder="e.g., 15"
                        data-testid="upm-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">Efficiency %</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={throughputConfig.efficiency_rate}
                        onChange={(e) => handleUpdateThroughput('efficiency_rate', parseFloat(e.target.value) || 85)}
                        className="h-8 text-sm mt-1"
                        placeholder="85"
                        data-testid="efficiency-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">Hours/Shift</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={throughputConfig.hours_per_shift}
                        onChange={(e) => handleUpdateThroughput('hours_per_shift', parseFloat(e.target.value) || 8)}
                        className="h-8 text-sm mt-1"
                        placeholder="8"
                        data-testid="hours-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">Units/Shift</Label>
                      <div className="h-8 flex items-center text-xl font-bold text-cyan-700 mt-1 bg-white rounded px-3 border" data-testid="throughput-result">
                        {(throughputConfig.calculated_throughput || throughputUnitsPerShift || 0).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-600 bg-white rounded p-2 border">
                    <strong>Formula:</strong> {throughputConfig.units_per_minute || 0} UPM × {throughputConfig.efficiency_rate || 85}% × 60 min × {throughputConfig.hours_per_shift || 8} hrs = <strong className="text-cyan-700">{(throughputConfig.calculated_throughput || throughputUnitsPerShift || 0).toLocaleString()} units/shift</strong>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Labor Costing Card */}
          <Card className="shadow-sm border-l-4 border-l-indigo-500">
            <CardHeader className="py-3 px-4 cursor-pointer bg-gradient-to-r from-indigo-50 to-white" onClick={() => setShowLaborSection(!showLaborSection)}>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Users className="w-5 h-5 text-indigo-600" />
                  Labor Costing
                  {laborCostBreakdown && laborCostBreakdown.labor_cost_per_unit > 0 && (
                    <Badge className="bg-indigo-100 text-indigo-700 ml-2">
                      ${laborCostBreakdown.labor_cost_per_unit?.toFixed(4)}/unit
                    </Badge>
                  )}
                </CardTitle>
                <div className="flex items-center gap-1">
                  {showLaborSection && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); setShowLaborSettingsModal(true); }} title="Labor Settings">
                      <Settings className="w-3 h-3 mr-1" />Settings
                    </Button>
                  )}
                  <Badge variant="outline" className="text-xs ml-1">
                    {showLaborSection ? '▼' : '▶'}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            
            {showLaborSection && (
              <CardContent className="pt-0">
                {/* Labor Lines Table */}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-600">Shift Labor</span>
                  <Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => setShowAddLaborLineModal(true)}>
                    <Plus className="w-3 h-3 mr-1" />Add Role
                  </Button>
                </div>
                
                <div className="border rounded overflow-hidden mb-4">
                  <table className="w-full text-xs" data-testid="labor-cost-table">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="w-6"></th>
                        <th className="text-left px-2 py-1.5 font-medium text-gray-600">Employee Type</th>
                        <th className="text-center px-2 py-1.5 font-medium text-gray-600 w-20"># Employees</th>
                        <th className="text-right px-2 py-1.5 font-medium text-gray-600 w-20">Hourly Rate</th>
                        <th className="text-right px-2 py-1.5 font-medium text-indigo-600 bg-indigo-50 w-24">Line Cost</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleLaborDragEnd}>
                      <SortableContext items={laborLines.map(l => l.id)} strategy={verticalListSortingStrategy}>
                        <tbody className="divide-y">
                          {laborLines.length === 0 ? (
                            <tr>
                              <td colSpan="6" className="px-2 py-4 text-center text-gray-400">
                                No labor lines added. Click "Add Role" to start.
                              </td>
                            </tr>
                          ) : (
                            laborLines.map((line) => {
                              const breakdown = laborCostBreakdown?.lines?.find(b => b.id === line.id);
                              return (
                                <SortableTableRow key={line.id} id={line.id} className="hover:bg-gray-50" data-testid={`labor-line-${line.id}`}>
                                  <td className="px-2 py-1 font-medium">
                                    {line.employee_type}
                                    {line.is_rate_overridden && (
                                      <Badge className="ml-1 text-[9px] bg-amber-100 text-amber-700">custom rate</Badge>
                                    )}
                                  </td>
                                  <td className="px-1 py-1">
                                    <Input
                                      type="number"
                                      step="0.5"
                                      min="0"
                                      value={line.employee_count}
                                      onChange={(e) => handleUpdateLaborLine(line.id, 'employee_count', parseFloat(e.target.value) || 0)}
                                      className="h-6 text-center text-xs w-16 px-1"
                                    />
                                  </td>
                                  <td className="px-1 py-1">
                                    <div className="flex items-center justify-end gap-1">
                                      <span className="text-gray-400">$</span>
                                      <Input
                                        type="number"
                                        step="0.50"
                                        min="0"
                                        value={line.hourly_rate}
                                        onChange={(e) => handleUpdateLaborLine(line.id, 'hourly_rate', parseFloat(e.target.value) || 0)}
                                        className="h-6 text-right text-xs w-16 px-1"
                                      />
                                    </div>
                                  </td>
                                  <td className="px-2 py-1 text-right font-medium text-indigo-600 bg-indigo-50">
                                    ${breakdown?.line_cost?.toFixed(2) || '0.00'}
                                  </td>
                                  <td className="px-1 py-1">
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                                      onClick={() => handleDeleteLaborLine(line.id)}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </td>
                                </SortableTableRow>
                              );
                            })
                          )}
                        </tbody>
                      </SortableContext>
                    </DndContext>
                    {laborLines.length > 0 && laborCostBreakdown && (
                      <tfoot className="border-t font-semibold text-xs bg-indigo-50">
                        <tr>
                          <td></td>
                          <td className="px-2 py-1.5 text-gray-700">Total Shift Labor</td>
                          <td colSpan="2" className="px-2 py-1.5 text-right text-gray-500 text-[10px]">
                            {laborCostBreakdown.settings_used?.hours_per_shift}hrs × {laborCostBreakdown.settings_used?.tax_burden_multiplier} tax mult
                          </td>
                          <td className="px-2 py-1.5 text-right text-indigo-700">
                            ${laborCostBreakdown.total_shift_labor_cost?.toFixed(2) || '0.00'}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
                
                {/* Labor Cost Summary */}
                <div className="bg-indigo-50 rounded-lg p-3 mt-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-gray-600">Total Shift Labor Cost</Label>
                      <div className="h-8 flex items-center text-lg font-semibold text-indigo-700 mt-1">
                        ${laborCostBreakdown?.total_shift_labor_cost?.toFixed(2) || '0.00'}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">Labor Cost/Unit</Label>
                      <div className="h-8 flex items-center text-lg font-semibold text-indigo-700 mt-1" data-testid="labor-cost-per-unit">
                        ${laborCostBreakdown?.labor_cost_per_unit?.toFixed(4) || '0.0000'}
                      </div>
                      <div className="text-[10px] text-gray-500">
                        ${laborCostBreakdown?.total_shift_labor_cost?.toFixed(2) || '0'} ÷ {(throughputConfig.calculated_throughput || throughputUnitsPerShift || 0).toLocaleString()} units
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Miscellaneous Costs Card */}
          <Card className="shadow-sm border-l-4 border-l-emerald-500">
            <CardHeader className="py-3 px-4 cursor-pointer bg-gradient-to-r from-emerald-50 to-white" onClick={() => setShowMiscCostsSection(!showMiscCostsSection)}>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                  Miscellaneous Costs
                  {miscCostBreakdown && miscCostBreakdown.total_per_unit > 0 && (
                    <Badge className="bg-emerald-100 text-emerald-700 ml-2">
                      ${miscCostBreakdown.total_per_unit?.toFixed(4)}/unit
                    </Badge>
                  )}
                </CardTitle>
                <Badge variant="outline" className="text-xs">
                  {showMiscCostsSection ? '▼' : '▶'}
                </Badge>
              </div>
            </CardHeader>
            
            {showMiscCostsSection && (
              <CardContent className="pt-0">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-600">Cost Lines</span>
                  <Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => setShowAddMiscCostModal(true)}>
                    <Plus className="w-3 h-3 mr-1" />Add Cost
                  </Button>
                </div>
                
                <div className="border rounded overflow-hidden mb-3">
                  <table className="w-full text-xs" data-testid="misc-cost-table">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="w-6"></th>
                        <th className="text-left px-2 py-1.5 font-medium text-gray-600">Cost Type</th>
                        <th className="text-center px-2 py-1.5 font-medium text-gray-600 w-24">Method</th>
                        <th className="text-right px-2 py-1.5 font-medium text-gray-600 w-24">Value</th>
                        <th className="text-right px-2 py-1.5 font-medium text-emerald-600 bg-emerald-50 w-24">$/Unit</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleMiscCostDragEnd}>
                      <SortableContext items={miscCostLines.map(l => l.id)} strategy={verticalListSortingStrategy}>
                        <tbody className="divide-y">
                          {miscCostLines.length === 0 ? (
                            <tr>
                              <td colSpan="6" className="px-2 py-4 text-center text-gray-400">
                                No miscellaneous costs added. Click "Add Cost" to start.
                              </td>
                            </tr>
                          ) : (
                            miscCostLines.map(line => {
                              const breakdown = miscCostBreakdown?.lines?.find(b => b.id === line.id);
                              return (
                                <SortableTableRow key={line.id} id={line.id} className="hover:bg-gray-50">
                                  <td className="px-2 py-1 font-medium">{line.cost_type}</td>
                                  <td className="px-2 py-1 text-center">
                                    <Select 
                                      value={line.cost_method} 
                                      onValueChange={(val) => handleUpdateMiscCostLine(line.id, 'cost_method', val)}
                                    >
                                      <SelectTrigger className="h-6 text-[10px] w-24">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="per_unit">per unit</SelectItem>
                                        <SelectItem value="bulk">÷ throughput</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </td>
                                  <td className="px-1 py-1">
                                    <div className="flex items-center justify-end gap-1">
                                      <span className="text-gray-400">$</span>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={line.cost_value}
                                        onChange={(e) => handleUpdateMiscCostLine(line.id, 'cost_value', e.target.value)}
                                        className="h-6 text-right text-xs w-16 px-1"
                                      />
                                    </div>
                                  </td>
                                  <td className="px-2 py-1 text-right font-medium text-emerald-600 bg-emerald-50">
                                    ${breakdown?.cost_per_unit?.toFixed(4) || '0.0000'}
                                  </td>
                                  <td className="px-1 py-1">
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                                      onClick={() => handleDeleteMiscCostLine(line.id)}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </td>
                                </SortableTableRow>
                              );
                            })
                          )}
                        </tbody>
                      </SortableContext>
                    </DndContext>
                    {miscCostLines.length > 0 && miscCostBreakdown && (
                      <tfoot className="border-t font-semibold text-xs bg-emerald-50">
                        <tr>
                          <td></td>
                          <td className="px-2 py-1.5 text-gray-700">Total Misc Cost</td>
                          <td colSpan="2" className="px-2 py-1.5 text-right text-gray-500 text-[10px]">
                            {miscCostLines.filter(l => l.cost_method === 'bulk').length > 0 && (
                              <span>Bulk costs ÷ {(throughputConfig.calculated_throughput || throughputUnitsPerShift || 0).toLocaleString()} units</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-right text-emerald-700">
                            ${miscCostBreakdown.total_per_unit?.toFixed(4) || '0.0000'}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </CardContent>
            )}
          </Card>
        </div>
        {/* End of Left Column */}

        {/* Right Column - 1/3 Width, Sticky */}
        <div className="w-1/3">
          <div className="sticky top-4 space-y-4">
            {/* Unit Economics Card */}
            <Card className="shadow-sm border-l-4 border-l-slate-700 bg-gradient-to-br from-slate-50 to-slate-100">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Target className="w-5 h-5 text-slate-700" />
                  Unit Economics
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {/* Pricing Strategy Toggle */}
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xs text-gray-600">Strategy:</span>
                  <div className="flex border rounded-lg overflow-hidden">
                    <button
                      className={`px-3 py-1 text-xs font-medium transition-all ${
                        pricingConfig.pricing_strategy === 'tolling' 
                          ? 'bg-slate-700 text-white' 
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                      }`}
                      onClick={() => handleUpdatePricing('pricing_strategy', 'tolling')}
                    >
                      Tolling
                    </button>
                    <button
                      className={`px-3 py-1 text-xs font-medium transition-all border-l ${
                        pricingConfig.pricing_strategy === 'turnkey' 
                          ? 'bg-slate-700 text-white' 
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                      }`}
                      onClick={() => handleUpdatePricing('pricing_strategy', 'turnkey')}
                    >
                      Turnkey
                    </button>
                  </div>
                </div>

                {/* Cost Breakdown Table */}
                <div className="border rounded overflow-hidden mb-4">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 border-b">
                      <tr>
                        <th className="text-left px-2 py-1.5 font-medium text-gray-600">Component</th>
                        <th className="text-right px-2 py-1.5 font-medium text-gray-600">$/Unit</th>
                        <th className="text-right px-2 py-1.5 font-medium text-gray-600 w-12">%</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      <tr className="hover:bg-gray-50">
                        <td className="px-2 py-1.5 flex items-center gap-1">
                          <FlaskConical className="w-3 h-3 text-amber-500" />
                          Food
                        </td>
                        <td className="px-2 py-1.5 text-right">${totalCostBreakdown?.cost_breakdown?.formulation?.toFixed(4) || '0.0000'}</td>
                        <td className="px-2 py-1.5 text-right text-gray-500">
                          {totalCostBreakdown?.total_cost_per_unit > 0 
                            ? ((totalCostBreakdown?.cost_breakdown?.formulation / totalCostBreakdown?.total_cost_per_unit) * 100).toFixed(0)
                            : '0'}%
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-2 py-1.5 flex items-center gap-1">
                          <Package className="w-3 h-3 text-purple-500" />
                          Packaging
                        </td>
                        <td className="px-2 py-1.5 text-right">${totalCostBreakdown?.cost_breakdown?.packaging?.toFixed(4) || '0.0000'}</td>
                        <td className="px-2 py-1.5 text-right text-gray-500">
                          {totalCostBreakdown?.total_cost_per_unit > 0 
                            ? ((totalCostBreakdown?.cost_breakdown?.packaging / totalCostBreakdown?.total_cost_per_unit) * 100).toFixed(0)
                            : '0'}%
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-2 py-1.5 flex items-center gap-1">
                          <Users className="w-3 h-3 text-indigo-500" />
                          Labor
                        </td>
                        <td className="px-2 py-1.5 text-right">${totalCostBreakdown?.cost_breakdown?.labor?.toFixed(4) || '0.0000'}</td>
                        <td className="px-2 py-1.5 text-right text-gray-500">
                          {totalCostBreakdown?.total_cost_per_unit > 0 
                            ? ((totalCostBreakdown?.cost_breakdown?.labor / totalCostBreakdown?.total_cost_per_unit) * 100).toFixed(0)
                            : '0'}%
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-2 py-1.5 flex items-center gap-1">
                          <DollarSign className="w-3 h-3 text-emerald-500" />
                          Misc
                        </td>
                        <td className="px-2 py-1.5 text-right">${totalCostBreakdown?.cost_breakdown?.misc?.toFixed(4) || '0.0000'}</td>
                        <td className="px-2 py-1.5 text-right text-gray-500">
                          {totalCostBreakdown?.total_cost_per_unit > 0 
                            ? ((totalCostBreakdown?.cost_breakdown?.misc / totalCostBreakdown?.total_cost_per_unit) * 100).toFixed(0)
                            : '0'}%
                        </td>
                      </tr>
                      {pricingConfig.pricing_strategy === 'tolling' && (
                        <tr className="hover:bg-gray-50">
                          <td className="px-2 py-1.5 flex items-center gap-1">
                            <TrendingUp className="w-3 h-3 text-slate-500" />
                            Overhead
                          </td>
                          <td className="px-2 py-1.5 text-right">${totalCostBreakdown?.cost_breakdown?.overhead?.toFixed(4) || '0.0000'}</td>
                          <td className="px-2 py-1.5 text-right text-gray-500">
                            {totalCostBreakdown?.total_cost_per_unit > 0 
                              ? ((totalCostBreakdown?.cost_breakdown?.overhead / totalCostBreakdown?.total_cost_per_unit) * 100).toFixed(0)
                              : '0'}%
                          </td>
                        </tr>
                      )}
                      {/* Brokerage Row - shows if broker_rate > 0 */}
                      {(totalCostBreakdown?.brokerage_rate > 0 || project?.broker_rate > 0) && (
                        <tr className="hover:bg-gray-50 bg-amber-50">
                          <td className="px-2 py-1.5 flex items-center gap-1">
                            <Percent className="w-3 h-3 text-amber-600" />
                            Brokerage ({totalCostBreakdown?.brokerage_rate || project?.broker_rate || 0}%)
                          </td>
                          <td className="px-2 py-1.5 text-right">${totalCostBreakdown?.cost_breakdown?.brokerage?.toFixed(4) || '0.0000'}</td>
                          <td className="px-2 py-1.5 text-right text-amber-600">
                            {totalCostBreakdown?.selling_price > 0 
                              ? ((totalCostBreakdown?.cost_breakdown?.brokerage / totalCostBreakdown?.selling_price) * 100).toFixed(1)
                              : '0'}%
                          </td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot className="border-t-2 font-semibold bg-slate-100">
                      <tr>
                        <td className="px-2 py-1.5 text-slate-700">Total</td>
                        <td className="px-2 py-1.5 text-right text-slate-700">${totalCostBreakdown?.total_cost_per_unit?.toFixed(4) || '0.0000'}</td>
                        <td className="px-2 py-1.5 text-right text-slate-700">100%</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Pricing Summary - Turnkey Mode */}
                {pricingConfig.pricing_strategy === 'turnkey' && (
                  <div className="bg-emerald-50 rounded-lg p-3 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600 font-medium">Selling Price</span>
                      <div className="flex items-center gap-1">
                        <span className="text-gray-400">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={pricingConfig.turnkey_price}
                          onChange={(e) => handleUpdatePricing('turnkey_price', parseFloat(e.target.value) || 0)}
                          className="h-7 text-right text-sm font-bold w-24 px-1"
                        />
                      </div>
                    </div>
                    <div className="flex justify-between text-sm border-t pt-2 mt-1">
                      <span className="text-gray-600">Gross Margin</span>
                      <span className={`font-semibold ${
                        (totalCostBreakdown?.margin_percent || 0) < 20 ? 'text-red-600' :
                        (totalCostBreakdown?.margin_percent || 0) < 25 ? 'text-orange-500' :
                        'text-emerald-600'
                      }`}>
                        ${totalCostBreakdown?.margin_per_unit?.toFixed(2) || '0.00'} ({totalCostBreakdown?.margin_percent?.toFixed(1) || '0'}%)
                      </span>
                    </div>
                    {/* Margin health indicator */}
                    <div className="text-[10px] text-right">
                      {(totalCostBreakdown?.margin_percent || 0) < 20 && (
                        <span className="text-red-500">⚠ Below target (&lt;20%)</span>
                      )}
                      {(totalCostBreakdown?.margin_percent || 0) >= 20 && (totalCostBreakdown?.margin_percent || 0) < 25 && (
                        <span className="text-orange-500">● Marginal (20-25%)</span>
                      )}
                      {(totalCostBreakdown?.margin_percent || 0) >= 25 && (
                        <span className="text-emerald-500">✓ Healthy (&gt;25%)</span>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Pricing Summary - Tolling Mode */}
                {pricingConfig.pricing_strategy === 'tolling' && (
                  <div className="bg-slate-100 rounded-lg p-3 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600">Overhead/Shift</span>
                      <div className="flex items-center gap-1">
                        <span className="text-gray-400">$</span>
                        <Input
                          type="number"
                          step="100"
                          value={pricingConfig.overhead_per_shift}
                          onChange={(e) => handleUpdatePricing('overhead_per_shift', parseFloat(e.target.value) || 0)}
                          className="h-6 text-right text-sm w-20 px-1"
                        />
                      </div>
                    </div>
                    <div className="flex justify-between text-sm border-t pt-2 mt-2">
                      <span className="text-gray-600">Toll Fee</span>
                      <span className="font-semibold text-indigo-600">
                        ${((totalCostBreakdown?.cost_breakdown?.overhead || 0) + 
                           (totalCostBreakdown?.cost_breakdown?.labor || 0) + 
                           (totalCostBreakdown?.cost_breakdown?.misc || 0) +
                           (totalCostBreakdown?.cost_breakdown?.brokerage || 0)).toFixed(2)}/unit
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 font-medium">Selling Price</span>
                      <span className="font-bold text-lg text-slate-800">
                        ${totalCostBreakdown?.selling_price?.toFixed(2) || '0.00'}
                      </span>
                    </div>
                    {/* Gross Margin for Tolling (no color coding) */}
                    <div className="flex justify-between text-sm border-t pt-2 mt-1">
                      <span className="text-gray-600">Gross Margin</span>
                      <span className="font-semibold text-slate-600">
                        ${totalCostBreakdown?.margin_per_unit?.toFixed(2) || '0.00'} ({totalCostBreakdown?.margin_percent?.toFixed(1) || '0'}%)
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Files Section */}
            <Card className="shadow-sm">
              <CardHeader className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Paperclip className="w-4 h-4 text-gray-500" />
                    Files
                    <Badge variant="outline" className="text-[10px]">{files.length}</Badge>
                  </CardTitle>
                  <label className="cursor-pointer">
                    <input type="file" className="hidden" onChange={handleFileUpload} disabled={uploadingFile} />
                    <Button size="sm" variant="outline" className="h-6 text-xs" disabled={uploadingFile} asChild>
                      <span><Upload className="w-3 h-3 mr-1" />{uploadingFile ? 'Uploading...' : 'Upload'}</span>
                    </Button>
                  </label>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {files.length === 0 ? (
                    <div className="text-center py-3 text-gray-400 text-xs">No files uploaded</div>
                  ) : (
                    files.map(file => (
                      <div key={file.id} className="flex items-center justify-between p-1.5 rounded hover:bg-gray-50 text-xs">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          <span className="truncate">{file.original_filename}</span>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => handleDownloadFile(file.id, file.original_filename)}>
                            <Download className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-red-400 hover:text-red-600" onClick={() => handleDeleteFile(file.id, file.original_filename)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Activity Feed */}
            <Card className="shadow-sm">
              <CardHeader className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <History className="w-4 h-4 text-gray-500" />
                    Activity
                  </CardTitle>
                  <Select value={activityFilter} onValueChange={setActivityFilter}>
                    <SelectTrigger className="h-6 w-24 text-[10px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="add">Added</SelectItem>
                      <SelectItem value="update">Updated</SelectItem>
                      <SelectItem value="delete">Deleted</SelectItem>
                      <SelectItem value="submit">Submitted</SelectItem>
                      <SelectItem value="approve">Approved</SelectItem>
                      <SelectItem value="reject">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {activityLog.length === 0 ? (
                    <div className="text-center py-4 text-gray-400 text-xs">No activity yet</div>
                  ) : (
                    activityLog.map(activity => (
                      <div key={activity.id} className="flex items-start gap-2 p-1.5 rounded hover:bg-gray-50 text-[11px] border-l-2 border-l-slate-200">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                          activity.activity_type === 'add' ? 'bg-green-100 text-green-600' :
                          activity.activity_type === 'update' ? 'bg-blue-100 text-blue-600' :
                          activity.activity_type === 'delete' ? 'bg-red-100 text-red-600' :
                          activity.activity_type === 'submit' ? 'bg-amber-100 text-amber-600' :
                          activity.activity_type === 'approve' ? 'bg-emerald-100 text-emerald-600' :
                          activity.activity_type === 'reject' ? 'bg-red-100 text-red-600' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {activity.activity_type === 'add' && <Plus className="w-2.5 h-2.5" />}
                          {activity.activity_type === 'update' && <Settings className="w-2.5 h-2.5" />}
                          {activity.activity_type === 'delete' && <Trash2 className="w-2.5 h-2.5" />}
                          {activity.activity_type === 'submit' && <Send className="w-2.5 h-2.5" />}
                          {activity.activity_type === 'approve' && <CheckCircle className="w-2.5 h-2.5" />}
                          {activity.activity_type === 'reject' && <XCircle className="w-2.5 h-2.5" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-gray-700 truncate">{activity.description}</div>
                          {activity.old_value && activity.new_value && (
                            <div className="text-gray-400 text-[10px]">
                              <span className="line-through">{activity.old_value}</span> → <span className="text-green-600">{activity.new_value}</span>
                            </div>
                          )}
                          <div className="text-gray-400 text-[10px]">{new Date(activity.timestamp).toLocaleString()}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        {/* End of Right Column */}
      </div>
      {/* End of Main Content */}
      </div>
      {/* End of Main Page Content wrapper */}

      {/* Add Packaging Component Modal */}
      <Dialog open={showAddPackagingModal} onOpenChange={setShowAddPackagingModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">Add Packaging Component</DialogTitle>
          </DialogHeader>
          
          {/* Create New Component Button */}
          <div 
            className="p-3 border-2 border-dashed border-purple-300 rounded cursor-pointer hover:bg-purple-50 transition-colors mb-2"
            onClick={() => { setShowAddPackagingModal(false); setShowCreatePackagingComponentModal(true); }}
            data-testid="create-new-packaging-component"
          >
            <div className="flex items-center gap-2 text-purple-600">
              <Plus className="w-4 h-4" />
              <span className="font-medium text-sm">Create New Component</span>
            </div>
            <div className="text-xs text-gray-500 mt-1">Add a custom packaging component (pallet wrap, labels, etc.)</div>
          </div>
          
          <div className="text-xs text-gray-500 font-medium mb-1">Or select from existing:</div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {packagingComponents.length === 0 ? (
              <div className="text-center py-4 text-gray-500 text-sm">
                Loading components...
              </div>
            ) : (
              packagingComponents.map(comp => {
                const levelColors = {
                  unit: 'bg-blue-100 text-blue-700 border-blue-200',
                  carton: 'bg-orange-100 text-orange-700 border-orange-200',
                  case: 'bg-green-100 text-green-700 border-green-200',
                  pallet: 'bg-purple-100 text-purple-700 border-purple-200'
                };
                const alreadyAdded = packagingLines.some(l => l.component_id === comp.id);
                return (
                  <div 
                    key={comp.id}
                    className={`p-3 border rounded cursor-pointer transition-colors ${
                      alreadyAdded ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => !alreadyAdded && handleAddPackagingLine(comp)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-sm">{comp.name}</div>
                        {comp.description && <div className="text-xs text-gray-500">{comp.description}</div>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`text-[10px] ${levelColors[comp.level]}`}>{comp.level}</Badge>
                        <span className="text-sm font-medium">${comp.cost?.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPackagingModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create New Packaging Component Modal */}
      <Dialog open={showCreatePackagingComponentModal} onOpenChange={setShowCreatePackagingComponentModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">Create New Packaging Component</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Name *</Label>
              <Input
                value={newPackagingComponent.name}
                onChange={(e) => setNewPackagingComponent({ ...newPackagingComponent, name: e.target.value })}
                placeholder="e.g., Pallet Wrap, Shrink Band"
                className="h-8 text-sm"
                data-testid="new-packaging-name"
              />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Input
                value={newPackagingComponent.description}
                onChange={(e) => setNewPackagingComponent({ ...newPackagingComponent, description: e.target.value })}
                placeholder="Optional description"
                className="h-8 text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Level *</Label>
                <Select
                  value={newPackagingComponent.level}
                  onValueChange={(value) => setNewPackagingComponent({ ...newPackagingComponent, level: value })}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unit">Unit</SelectItem>
                    <SelectItem value="carton">Carton</SelectItem>
                    <SelectItem value="case">Case</SelectItem>
                    <SelectItem value="pallet">Pallet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Cost ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newPackagingComponent.cost}
                  onChange={(e) => setNewPackagingComponent({ ...newPackagingComponent, cost: parseFloat(e.target.value) || 0 })}
                  className="h-8 text-sm"
                  data-testid="new-packaging-cost"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Supplier</Label>
                <Input
                  value={newPackagingComponent.supplier}
                  onChange={(e) => setNewPackagingComponent({ ...newPackagingComponent, supplier: e.target.value })}
                  placeholder="Optional"
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">SKU</Label>
                <Input
                  value={newPackagingComponent.sku}
                  onChange={(e) => setNewPackagingComponent({ ...newPackagingComponent, sku: e.target.value })}
                  placeholder="Optional"
                  className="h-8 text-sm"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCreatePackagingComponentModal(false)}>Cancel</Button>
            <Button onClick={handleCreatePackagingComponent} data-testid="create-packaging-submit">
              <Plus className="w-3 h-3 mr-1" />Create & Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy Packaging to Item Modal */}
      <Dialog open={showCopyPackagingModal} onOpenChange={setShowCopyPackagingModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-base">Copy Packaging Config to Another Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Search R&D Items</Label>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search by item or project name..."
                  value={copySearchTerm}
                  onChange={(e) => setCopySearchTerm(e.target.value)}
                  className="pl-8 h-8 text-sm"
                />
              </div>
            </div>
            <div className="border rounded max-h-64 overflow-y-auto">
              {filteredRdItemsForCopy.length === 0 ? (
                <div className="text-center py-6 text-gray-500 text-sm">
                  {rdItemsForCopy.length === 0 ? 'Loading items...' : 'No matching items found'}
                </div>
              ) : (
                filteredRdItemsForCopy.map(item => (
                  <div 
                    key={item.id}
                    className={`p-3 border-b cursor-pointer transition-colors ${
                      copyTargetItemId === item.id ? 'bg-purple-50 border-purple-200' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => setCopyTargetItemId(item.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-sm">{item.name}</div>
                        <div className="text-xs text-gray-500">{item.project_name}</div>
                      </div>
                      {copyTargetItemId === item.id && (
                        <Check className="w-4 h-4 text-purple-600" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            {packagingLines.length > 0 && (
              <div className="bg-gray-50 p-2 rounded text-xs text-gray-600">
                <strong>Config to copy:</strong> {packagingLines.length} component(s), {packagingConfig.has_carton_level ? '4-level' : '3-level'} hierarchy
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowCopyPackagingModal(false); setCopyTargetItemId(''); setCopySearchTerm(''); }}>Cancel</Button>
            <Button onClick={handleCopyPackagingToItem} disabled={!copyTargetItemId}>
              <Copy className="w-3 h-3 mr-1" />Copy Config
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Template Modal */}
      <Dialog open={showSaveTemplateModal} onOpenChange={setShowSaveTemplateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">Save as Packaging Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Template Name *</Label>
              <Input
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                placeholder="e.g., Standard 6-Pack Carton"
                className="h-8 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Input
                value={newTemplateDescription}
                onChange={(e) => setNewTemplateDescription(e.target.value)}
                placeholder="Optional description"
                className="h-8 text-sm"
              />
            </div>
            <div className="bg-gray-50 p-2 rounded text-xs text-gray-600">
              <strong>Config to save:</strong> {packagingLines.length} component(s), {packagingConfig.has_carton_level ? '4-level' : '3-level'} hierarchy
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowSaveTemplateModal(false); setNewTemplateName(''); setNewTemplateDescription(''); }}>Cancel</Button>
            <Button onClick={handleSaveAsTemplate}>
              <Save className="w-3 h-3 mr-1" />Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Load Template Modal */}
      <Dialog open={showLoadTemplateModal} onOpenChange={setShowLoadTemplateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">Load Packaging Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {packagingTemplates.length === 0 ? (
              <div className="text-center py-6 text-gray-500 text-sm">
                No templates saved yet. Create one by configuring packaging and clicking "Template".
              </div>
            ) : (
              packagingTemplates.map(template => (
                <div 
                  key={template.id}
                  className="p-3 border rounded hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 cursor-pointer" onClick={() => handleLoadTemplate(template.id)}>
                      <div className="font-medium text-sm">{template.name}</div>
                      {template.description && <div className="text-xs text-gray-500">{template.description}</div>}
                      <div className="text-xs text-gray-400 mt-1">
                        {template.lines?.length || 0} components • {template.config?.has_carton_level ? '4-level' : '3-level'}
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                      onClick={() => handleDeleteTemplate(template.id)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLoadTemplateModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Labor Line Modal */}
      <Dialog open={showAddLaborLineModal} onOpenChange={setShowAddLaborLineModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">Add Labor Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Employee Type *</Label>
              <Select
                value={newLaborLine.employee_type}
                onValueChange={(value) => {
                  setNewLaborLine({
                    ...newLaborLine,
                    employee_type: value,
                    hourly_rate: laborSettings.default_wage_rates[value] || 0
                  });
                }}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Select or type custom..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(laborSettings.default_wage_rates).map(type => (
                    <SelectItem key={type} value={type}>
                      {type} (${laborSettings.default_wage_rates[type]?.toFixed(2)}/hr)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={newLaborLine.employee_type}
                onChange={(e) => setNewLaborLine({ ...newLaborLine, employee_type: e.target.value })}
                placeholder="Or enter custom type..."
                className="h-8 text-sm mt-2"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs"># of Employees</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  value={newLaborLine.employee_count}
                  onChange={(e) => setNewLaborLine({ ...newLaborLine, employee_count: parseFloat(e.target.value) || 0 })}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Hourly Rate ($)</Label>
                <Input
                  type="number"
                  step="0.50"
                  min="0"
                  value={newLaborLine.hourly_rate}
                  onChange={(e) => setNewLaborLine({ ...newLaborLine, hourly_rate: parseFloat(e.target.value) || 0 })}
                  className="h-8 text-sm"
                  placeholder={newLaborLine.employee_type && laborSettings.default_wage_rates[newLaborLine.employee_type] ? `Default: $${laborSettings.default_wage_rates[newLaborLine.employee_type]}` : ''}
                />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowAddLaborLineModal(false); setNewLaborLine({ employee_type: '', employee_count: 1, hourly_rate: 0 }); }}>Cancel</Button>
            <Button onClick={handleAddLaborLine}>
              <Plus className="w-3 h-3 mr-1" />Add Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Labor Settings Modal */}
      <Dialog open={showLaborSettingsModal} onOpenChange={setShowLaborSettingsModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-base">Labor Calculation Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Hours per Shift</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={laborSettings.hours_per_shift}
                  onChange={(e) => setLaborSettings({ ...laborSettings, hours_per_shift: parseFloat(e.target.value) || 8.5 })}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Tax Burden Multiplier</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={laborSettings.tax_burden_multiplier}
                  onChange={(e) => setLaborSettings({ ...laborSettings, tax_burden_multiplier: parseFloat(e.target.value) || 1.0765 })}
                  className="h-8 text-sm"
                />
                <div className="text-[10px] text-gray-500 mt-1">e.g., 1.0765 adds ~7.65% for employer taxes</div>
              </div>
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs">Default Wage Rates</Label>
              </div>
              <div className="border rounded max-h-48 overflow-y-auto">
                {Object.entries(laborSettings.default_wage_rates).map(([type, rate]) => (
                  <div key={type} className="flex items-center justify-between p-2 border-b last:border-b-0">
                    <span className="text-sm">{type}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">$</span>
                      <Input
                        type="number"
                        step="0.50"
                        value={rate}
                        onChange={(e) => handleAddDefaultWageRate(type, e.target.value)}
                        className="h-6 w-20 text-sm text-right"
                      />
                      <span className="text-xs text-gray-500">/hr</span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                        onClick={() => handleRemoveDefaultWageRate(type)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <Input
                  placeholder="New role type..."
                  className="h-7 text-sm flex-1"
                  id="new-wage-type"
                />
                <Input
                  type="number"
                  placeholder="Rate"
                  className="h-7 text-sm w-20"
                  id="new-wage-rate"
                />
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="h-7"
                  onClick={() => {
                    const typeInput = document.getElementById('new-wage-type');
                    const rateInput = document.getElementById('new-wage-rate');
                    if (typeInput.value && rateInput.value) {
                      handleAddDefaultWageRate(typeInput.value, rateInput.value);
                      typeInput.value = '';
                      rateInput.value = '';
                    }
                  }}
                >
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
            </div>
            
            <div className="bg-gray-50 p-2 rounded text-xs text-gray-600">
              <strong>Calculation:</strong> # Employees × Hourly Rate × {laborSettings.hours_per_shift} hours × {laborSettings.tax_burden_multiplier}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowLaborSettingsModal(false)}>Cancel</Button>
            <Button onClick={handleSaveLaborSettings}>
              <Save className="w-3 h-3 mr-1" />Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Miscellaneous Cost Modal */}
      <Dialog open={showAddMiscCostModal} onOpenChange={setShowAddMiscCostModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">Add Miscellaneous Cost</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Cost Type *</Label>
              <Select
                value={newMiscCostLine.cost_type}
                onValueChange={(value) => {
                  const typeConfig = miscCostTypes.find(t => t.name === value);
                  setNewMiscCostLine({
                    ...newMiscCostLine,
                    cost_type: value,
                    cost_method: typeConfig?.cost_method || 'per_unit',
                    cost_value: typeConfig?.default_cost || 0
                  });
                }}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Select cost type..." />
                </SelectTrigger>
                <SelectContent>
                  {miscCostTypes.map(type => (
                    <SelectItem key={type.id} value={type.name}>
                      {type.name} ({type.cost_method === 'bulk' ? 'Bulk' : 'Per Unit'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={newMiscCostLine.cost_type}
                onChange={(e) => setNewMiscCostLine({ ...newMiscCostLine, cost_type: e.target.value })}
                placeholder="Or enter custom type..."
                className="h-8 text-sm mt-2"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Cost Method</Label>
                <Select
                  value={newMiscCostLine.cost_method}
                  onValueChange={(value) => setNewMiscCostLine({ ...newMiscCostLine, cost_method: value })}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="per_unit">Per Unit (direct $/unit)</SelectItem>
                    <SelectItem value="bulk">Bulk (÷ throughput)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Cost Value ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newMiscCostLine.cost_value}
                  onChange={(e) => setNewMiscCostLine({ ...newMiscCostLine, cost_value: parseFloat(e.target.value) || 0 })}
                  className="h-8 text-sm"
                />
              </div>
            </div>
            <div className="bg-gray-50 p-2 rounded text-xs text-gray-600">
              {newMiscCostLine.cost_method === 'per_unit' 
                ? <span><strong>Per Unit:</strong> Cost is applied directly (${newMiscCostLine.cost_value?.toFixed(4) || 0}/unit)</span>
                : <span><strong>Bulk:</strong> ${newMiscCostLine.cost_value || 0} ÷ {(throughputConfig.calculated_throughput || throughputUnitsPerShift || 0).toLocaleString()} units = ${((newMiscCostLine.cost_value || 0) / ((throughputConfig.calculated_throughput || throughputUnitsPerShift) || 1)).toFixed(4)}/unit</span>
              }
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowAddMiscCostModal(false); setNewMiscCostLine({ cost_type: '', cost_method: 'per_unit', cost_value: 0 }); }}>Cancel</Button>
            <Button onClick={handleAddMiscCostLine}>
              <Plus className="w-3 h-3 mr-1" />Add Cost
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Line Modal */}
      <Dialog open={showAddLineModal} onOpenChange={setShowAddLineModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">Add Ingredient</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Search Existing Items</Label>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search items..."
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                  className="pl-8 h-8 text-sm"
                />
              </div>
              {itemSearch && (
                <div className="mt-1 max-h-32 overflow-y-auto border rounded text-sm">
                  {filteredExistingItems.slice(0, 8).map(item => (
                    <div 
                      key={item.id}
                      className="p-1.5 hover:bg-gray-100 cursor-pointer flex justify-between"
                      onClick={() => handleSelectExistingItem(item)}
                    >
                      <span className="font-medium text-xs">{item.name}</span>
                      <span className="text-[10px] text-gray-500">{item.sku}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {newLine.item_name && (
              <div className="p-2 bg-indigo-50 rounded text-sm">
                <span className="font-medium">{newLine.item_name}</span>
                <Badge className="ml-2 text-[10px]">{newLine.item_type}</Badge>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">BOM Qty (g)</Label>
                <Input type="number" value={newLine.bom_qty_grams} onChange={(e) => setNewLine({ ...newLine, bom_qty_grams: e.target.value })} className="h-8" />
              </div>
              <div>
                <Label className="text-xs">Yield %</Label>
                <Input type="number" step="0.1" value={newLine.yield_percent} onChange={(e) => setNewLine({ ...newLine, yield_percent: e.target.value })} className="h-8" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Cost UOM</Label>
                <Select value={newLine.cost_uom} onValueChange={(val) => {
                  const isFixed = FIXED_CONVERSIONS.hasOwnProperty(val);
                  setNewLine({ ...newLine, cost_uom: val, conversion_factor: isFixed ? FIXED_CONVERSIONS[val] : newLine.conversion_factor });
                }}>
                  <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>{COST_UOMS.map(uom => <SelectItem key={uom} value={uom}>{uom}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Conv. Factor</Label>
                <Input 
                  type="number" 
                  value={newLine.conversion_factor} 
                  onChange={(e) => setNewLine({ ...newLine, conversion_factor: e.target.value })} 
                  className="h-8" 
                  placeholder="g/UOM"
                  disabled={FIXED_CONVERSIONS.hasOwnProperty(newLine.cost_uom)}
                />
              </div>
              <div>
                <Label className="text-xs">$/UOM</Label>
                <Input type="number" step="0.01" value={newLine.cost_per_uom} onChange={(e) => setNewLine({ ...newLine, cost_per_uom: e.target.value })} className="h-8" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowAddLineModal(false)}>Cancel</Button>
            <Button size="sm" onClick={handleAddLine} disabled={!newLine.item_name}>Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Pending Item Modal */}
      <Dialog open={showCreatePendingModal} onOpenChange={setShowCreatePendingModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-yellow-500" />
              Create Pending Item
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500 -mt-2">Item doesn't exist yet. Create as pending to use in formulations.</p>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Item Name *</Label>
                <Input value={newPendingItem.name} onChange={(e) => setNewPendingItem({ ...newPendingItem, name: e.target.value })} className="h-8" />
              </div>
              <div>
                <Label className="text-xs">SKU</Label>
                <Input value={newPendingItem.sku} onChange={(e) => setNewPendingItem({ ...newPendingItem, sku: e.target.value })} className="h-8" placeholder="e.g., RM-001" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Category</Label>
                <Input value={newPendingItem.category} onChange={(e) => setNewPendingItem({ ...newPendingItem, category: e.target.value })} className="h-8" />
              </div>
              <div>
                <Label className="text-xs">Est. Cost</Label>
                <Input type="number" step="0.01" value={newPendingItem.estimated_cost} onChange={(e) => setNewPendingItem({ ...newPendingItem, estimated_cost: e.target.value })} className="h-8" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Ingredient Statement</Label>
              <p className="text-[10px] text-gray-400 mb-1">Paste the full ingredient list - allergens will be auto-detected</p>
              <Textarea 
                value={newPendingItem.ingredient_statement} 
                onChange={(e) => handleIngredientStatementChange(setNewPendingItem, newPendingItem, e.target.value)} 
                className="h-20 text-xs" 
                placeholder="e.g., Whole Eggs, Skim Milk, Soybean Oil, Salt..."
              />
            </div>
            
            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-medium">Major Allergens (FDA 9)</Label>
                {newPendingItem.allergens_auto_detected && (
                  <Badge className="bg-blue-100 text-blue-700 text-[10px] flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Auto-detected - Please Review
                  </Badge>
                )}
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                {ALLERGEN_CONFIG.map(allergen => (
                  <label 
                    key={allergen.id}
                    className={`flex items-center gap-2 p-1.5 rounded border cursor-pointer transition-colors text-xs ${
                      newPendingItem.allergens[allergen.id] ? 'bg-orange-50 border-orange-300' : 'hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={newPendingItem.allergens[allergen.id] || false}
                      onChange={() => setNewPendingItem(prev => ({
                        ...prev,
                        allergens: { ...prev.allergens, [allergen.id]: !prev.allergens[allergen.id] }
                      }))}
                      className="w-3 h-3 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    />
                    <span>{allergen.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowCreatePendingModal(false)}>Cancel</Button>
            <Button size="sm" onClick={handleCreatePendingItem} disabled={!newPendingItem.name}>Create & Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Version Modal */}
      <Dialog open={showNewVersionModal} onOpenChange={setShowNewVersionModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base">Create New Version</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500">Copy current formulation to a new version.</p>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Version Name</Label>
              <Input value={newVersion.name} onChange={(e) => setNewVersion({ ...newVersion, name: e.target.value })} className="h-8" placeholder="e.g., Cost Reduction" />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea value={newVersion.notes} onChange={(e) => setNewVersion({ ...newVersion, notes: e.target.value })} className="h-16 text-sm" placeholder="What changes?" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowNewVersionModal(false)}>Cancel</Button>
            <Button size="sm" onClick={handleCreateNewVersion}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compare Modal */}
      <Dialog open={showCompareModal} onOpenChange={setShowCompareModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitCompare className="w-5 h-5 text-purple-600" />
              Compare Formulations
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left px-3 py-2 border font-medium">Ingredient</th>
                  {compareFormulations.map(form => (
                    <th key={form.id} className="text-center px-3 py-2 border font-medium min-w-[120px]">
                      <div>{form.version}</div>
                      <div className="text-[10px] text-gray-500 font-normal">{form.name}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {getCompareIngredients().map((ing, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-3 py-1.5 border">
                      <div className="font-medium">{ing.name}</div>
                      <div className="text-[10px] text-gray-500">{ing.category}</div>
                    </td>
                    {compareFormulations.map(form => {
                      const line = getLineForIngredient(form.id, ing.name);
                      if (!line) {
                        return <td key={form.id} className="px-3 py-1.5 border text-center text-gray-400">—</td>;
                      }
                      const bomCost = calculateBomCost(line.bom_qty_grams, line.conversion_factor, line.cost_per_uom, line.yield_percent);
                      return (
                        <td key={form.id} className="px-3 py-1.5 border text-center">
                          <div className="font-medium">{line.bom_qty_grams}g</div>
                          <div className="text-green-600">${bomCost.toFixed(3)}</div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-100 font-semibold">
                  <td className="px-3 py-2 border">Total</td>
                  {compareFormulations.map(form => {
                    const lines = compareData[form.id] || [];
                    const totalW = lines.reduce((s, l) => s + (l.bom_qty_grams || 0), 0);
                    const totalC = lines.reduce((s, l) => s + calculateBomCost(l.bom_qty_grams, l.conversion_factor, l.cost_per_uom, l.yield_percent), 0);
                    return (
                      <td key={form.id} className="px-3 py-2 border text-center">
                        <div>{totalW.toFixed(0)}g</div>
                        <div className={totalC <= (item?.target_cost || 0) ? 'text-green-600' : 'text-red-600'}>
                          ${totalC.toFixed(2)}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCompareModal(false); setCompareFormulations([]); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ingredient Detail Modal - Full Edit */}
      <Dialog open={showIngredientDetailModal} onOpenChange={setShowIngredientDetailModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-purple-600" />
              Ingredient Details
            </DialogTitle>
          </DialogHeader>
          {selectedIngredient && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">{selectedIngredient.item_name}</h3>
                  <div className="flex gap-2 mt-1">
                    <Badge variant="outline">{selectedIngredient.category || 'Uncategorized'}</Badge>
                    {selectedIngredient.item_type === 'pending' && (
                      <Badge className="bg-yellow-100 text-yellow-700">Pending Item</Badge>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Editable Fields */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">BOM Quantity (g)</Label>
                  <Input
                    type="number"
                    value={selectedIngredient.bom_qty_grams}
                    onChange={(e) => {
                      const newVal = parseFloat(e.target.value) || 0;
                      setSelectedIngredient({...selectedIngredient, bom_qty_grams: newVal});
                      handleUpdateLine(selectedIngredient.id, 'bom_qty_grams', newVal);
                    }}
                    className="h-8"
                  />
                </div>
                <div>
                  <Label className="text-xs">Cost per {selectedIngredient.cost_uom}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={selectedIngredient.cost_per_uom}
                    onChange={(e) => {
                      const newVal = parseFloat(e.target.value) || 0;
                      setSelectedIngredient({...selectedIngredient, cost_per_uom: newVal});
                      handleUpdateLine(selectedIngredient.id, 'cost_per_uom', newVal);
                    }}
                    className="h-8"
                  />
                </div>
                <div>
                  <Label className="text-xs">Yield %</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={selectedIngredient.yield_percent}
                    onChange={(e) => {
                      const newVal = parseFloat(e.target.value) || 100;
                      setSelectedIngredient({...selectedIngredient, yield_percent: newVal});
                      handleUpdateLine(selectedIngredient.id, 'yield_percent', newVal);
                    }}
                    className="h-8"
                  />
                </div>
              </div>
              
              {/* Cost Summary */}
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-gray-50 rounded p-3 text-center">
                  <div className="text-gray-500 text-xs">UOM</div>
                  <div className="font-semibold">{selectedIngredient.cost_uom}</div>
                </div>
                <div className="bg-gray-50 rounded p-3 text-center">
                  <div className="text-gray-500 text-xs">Conversion</div>
                  <div className="font-semibold">{selectedIngredient.conversion_factor?.toFixed(2)}</div>
                </div>
                <div className="bg-gray-50 rounded p-3 text-center">
                  <div className="text-gray-500 text-xs">$/UOM</div>
                  <div className="font-semibold">${selectedIngredient.cost_per_uom?.toFixed(2)}</div>
                </div>
                <div className="bg-green-50 rounded p-3 text-center">
                  <div className="text-gray-500 text-xs">BOM Cost</div>
                  <div className="font-semibold text-green-600">
                    ${calculateBomCost(selectedIngredient.bom_qty_grams, selectedIngredient.conversion_factor, selectedIngredient.cost_per_uom, selectedIngredient.yield_percent).toFixed(3)}
                  </div>
                </div>
              </div>
              
              {/* Ingredient Statement */}
              <div>
                <Label className="text-xs">Ingredient Statement (Sub-ingredients)</Label>
                <Textarea
                  value={selectedIngredient.ingredient_statement || ''}
                  onChange={(e) => {
                    setSelectedIngredient({...selectedIngredient, ingredient_statement: e.target.value});
                    handleUpdateLine(selectedIngredient.id, 'ingredient_statement', e.target.value);
                  }}
                  placeholder="e.g., Whole Eggs, Skim Milk, Soybean Oil, Salt"
                  className="h-16 text-sm mt-1"
                />
              </div>
              
              {/* Allergens */}
              <div>
                <Label className="text-xs">Allergens (FDA 9)</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {ALLERGEN_CONFIG.map(allergen => {
                    const currentAllergens = Array.isArray(selectedIngredient.allergens) ? selectedIngredient.allergens : [];
                    const isChecked = currentAllergens.includes(allergen.label);
                    return (
                      <label 
                        key={allergen.id}
                        className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                          isChecked ? 'bg-orange-50 border-orange-300' : 'hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {
                            const newAllergens = isChecked 
                              ? currentAllergens.filter(a => a !== allergen.label)
                              : [...currentAllergens, allergen.label];
                            setSelectedIngredient({...selectedIngredient, allergens: newAllergens});
                            handleUpdateLine(selectedIngredient.id, 'allergens', newAllergens);
                          }}
                          className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        />
                        <span className="text-sm">{allergen.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              
              {/* Linked Nutrition Data */}
              <div className="border-t pt-4">
                <Label className="text-xs text-gray-500">Linked Nutrition Data</Label>
                <NutritionLookup ingredientName={selectedIngredient.item_name} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIngredientDetailModal(false)}>Close</Button>
            <Button onClick={() => { handleSaveFormulation(); setShowIngredientDetailModal(false); }}>
              <Save className="w-4 h-4 mr-1" />Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Nutrition Label Modal */}
      <Dialog open={showNutritionModal} onOpenChange={setShowNutritionModal}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-600" />
              Nutrition Facts Label Generator
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="preview" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="preview">Preview</TabsTrigger>
              <TabsTrigger value="linking">
                Data Linking
                {nutritionData && (
                  <Badge className={`ml-2 text-[10px] ${nutritionData.linked_count === nutritionData.total_ingredients ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    {nutritionData.linked_count}/{nutritionData.total_ingredients}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="ingredients">Ingredients</TabsTrigger>
            </TabsList>
            
            <TabsContent value="preview" className="space-y-4">
              <div className="flex gap-6">
                {/* FDA Nutrition Facts Label - Dynamic based on selected format */}
                <div className="flex-shrink-0">
                  {nutritionLoading ? (
                    <div className="w-64 h-96 border-2 border-black flex items-center justify-center bg-white">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                    </div>
                  ) : selectedLabelFormat === "tabular" ? (
                    /* Tabular Format Preview */
                    <div className="border-2 border-black p-2 w-80 bg-white font-sans text-[10px]">
                      <div className="text-lg font-black border-b-4 border-black pb-1">Nutrition Facts</div>
                      <div className="flex justify-between text-[9px] border-b border-black py-0.5">
                        <span>Serving: {servingSize.description} ({Math.round(servingSize.grams)}g)</span>
                        <span>Servings: 1</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 py-1 border-b border-black">
                        <div className="font-bold">Amount/Serving</div>
                        <div className="font-bold">Amount/Serving</div>
                      </div>
                      <div className="text-lg font-black py-1 border-b-2 border-black">
                        Calories {nutritionData?.nutrition ? Math.round(nutritionData.nutrition.calories) : '---'}
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 py-1">
                        <div>
                          <div className="flex justify-between"><span className="font-bold">Total Fat</span><span>{nutritionData?.nutrition?.total_fat || 0}g</span><span>{nutritionData?.daily_values?.total_fat || 0}%</span></div>
                          <div className="flex justify-between pl-2"><span>Sat. Fat</span><span>{nutritionData?.nutrition?.saturated_fat || 0}g</span><span>{nutritionData?.daily_values?.saturated_fat || 0}%</span></div>
                          <div className="flex justify-between pl-2"><span>Trans Fat</span><span>{nutritionData?.nutrition?.trans_fat || 0}g</span></div>
                          <div className="flex justify-between"><span className="font-bold">Cholest.</span><span>{Math.round(nutritionData?.nutrition?.cholesterol || 0)}mg</span><span>{nutritionData?.daily_values?.cholesterol || 0}%</span></div>
                          <div className="flex justify-between"><span className="font-bold">Sodium</span><span>{Math.round(nutritionData?.nutrition?.sodium || 0)}mg</span><span>{nutritionData?.daily_values?.sodium || 0}%</span></div>
                        </div>
                        <div>
                          <div className="flex justify-between"><span className="font-bold">Total Carb.</span><span>{nutritionData?.nutrition?.total_carbs || 0}g</span><span>{nutritionData?.daily_values?.total_carbs || 0}%</span></div>
                          <div className="flex justify-between pl-2"><span>Fiber</span><span>{nutritionData?.nutrition?.dietary_fiber || 0}g</span><span>{nutritionData?.daily_values?.dietary_fiber || 0}%</span></div>
                          <div className="flex justify-between pl-2"><span>Sugars</span><span>{nutritionData?.nutrition?.sugars || 0}g</span></div>
                          <div className="flex justify-between"><span className="font-bold">Protein</span><span>{nutritionData?.nutrition?.protein || 0}g</span></div>
                        </div>
                      </div>
                      <div className="border-t-2 border-black pt-1 flex justify-between text-[9px]">
                        <span>Vit D {nutritionData?.daily_values?.vitamin_d || 0}%</span>
                        <span>Calcium {nutritionData?.daily_values?.calcium || 0}%</span>
                        <span>Iron {nutritionData?.daily_values?.iron || 0}%</span>
                        <span>Potas. {nutritionData?.daily_values?.potassium || 0}%</span>
                      </div>
                    </div>
                  ) : selectedLabelFormat === "linear" ? (
                    /* Linear Format Preview */
                    <div className="border border-black p-2 w-96 bg-white font-sans text-[9px]">
                      <div className="flex items-center gap-2 border-b border-black pb-1">
                        <span className="font-black text-sm">Nutrition Facts</span>
                        <span>Serv. Size {servingSize.description} ({Math.round(servingSize.grams)}g)</span>
                      </div>
                      <div className="py-1 font-bold">
                        Calories {nutritionData?.nutrition ? Math.round(nutritionData.nutrition.calories) : '---'} | 
                        Fat {nutritionData?.nutrition?.total_fat || 0}g ({nutritionData?.daily_values?.total_fat || 0}%) | 
                        Sat Fat {nutritionData?.nutrition?.saturated_fat || 0}g ({nutritionData?.daily_values?.saturated_fat || 0}%) | 
                        Trans Fat {nutritionData?.nutrition?.trans_fat || 0}g | 
                        Cholest {Math.round(nutritionData?.nutrition?.cholesterol || 0)}mg ({nutritionData?.daily_values?.cholesterol || 0}%)
                      </div>
                      <div className="py-1">
                        Sodium {Math.round(nutritionData?.nutrition?.sodium || 0)}mg ({nutritionData?.daily_values?.sodium || 0}%) | 
                        Carb {nutritionData?.nutrition?.total_carbs || 0}g ({nutritionData?.daily_values?.total_carbs || 0}%) | 
                        Fiber {nutritionData?.nutrition?.dietary_fiber || 0}g ({nutritionData?.daily_values?.dietary_fiber || 0}%) | 
                        Sugars {nutritionData?.nutrition?.sugars || 0}g | 
                        Protein {nutritionData?.nutrition?.protein || 0}g
                      </div>
                      <div className="border-t border-black pt-1 text-[8px]">
                        Vit D {nutritionData?.daily_values?.vitamin_d || 0}% • Calcium {nutritionData?.daily_values?.calcium || 0}% • Iron {nutritionData?.daily_values?.iron || 0}% • Potassium {nutritionData?.daily_values?.potassium || 0}% | *%DV based on 2,000 cal diet
                      </div>
                    </div>
                  ) : selectedLabelFormat === "dual" ? (
                    /* Dual Column Format Preview */
                    <div className="border-2 border-black p-2 w-72 bg-white font-sans text-[10px]">
                      <div className="text-xl font-black border-b-8 border-black pb-1">Nutrition Facts</div>
                      <div className="text-[9px] border-b border-black py-1">1 serving per container</div>
                      <div className="flex justify-between font-bold text-[9px] border-b border-black py-1">
                        <span>Serving size</span>
                        <span>{servingSize.description} ({Math.round(servingSize.grams)}g)</span>
                      </div>
                      <div className="grid grid-cols-3 gap-1 text-[8px] font-bold border-b border-black py-1">
                        <span></span>
                        <span className="text-right">Per Serving</span>
                        <span className="text-right">Per Container</span>
                      </div>
                      <div className="border-b-4 border-black py-1">
                        <div className="grid grid-cols-3 gap-1 items-center">
                          <span className="text-base font-black">Calories</span>
                          <span className="text-right text-lg font-black">{nutritionData?.nutrition ? Math.round(nutritionData.nutrition.calories) : '---'}</span>
                          <span className="text-right text-lg font-black">{nutritionData?.nutrition ? Math.round(nutritionData.nutrition.calories) : '---'}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-1 text-[8px] font-bold border-b border-black py-0.5">
                        <span></span>
                        <span className="text-right">% DV*</span>
                        <span className="text-right">% DV*</span>
                      </div>
                      {[
                        { label: "Total Fat", val: `${nutritionData?.nutrition?.total_fat || 0}g`, dv: nutritionData?.daily_values?.total_fat || 0 },
                        { label: "  Sat. Fat", val: `${nutritionData?.nutrition?.saturated_fat || 0}g`, dv: nutritionData?.daily_values?.saturated_fat || 0, indent: true },
                        { label: "Cholesterol", val: `${Math.round(nutritionData?.nutrition?.cholesterol || 0)}mg`, dv: nutritionData?.daily_values?.cholesterol || 0 },
                        { label: "Sodium", val: `${Math.round(nutritionData?.nutrition?.sodium || 0)}mg`, dv: nutritionData?.daily_values?.sodium || 0 },
                        { label: "Total Carb.", val: `${nutritionData?.nutrition?.total_carbs || 0}g`, dv: nutritionData?.daily_values?.total_carbs || 0 },
                        { label: "  Fiber", val: `${nutritionData?.nutrition?.dietary_fiber || 0}g`, dv: nutritionData?.daily_values?.dietary_fiber || 0, indent: true },
                        { label: "Protein", val: `${nutritionData?.nutrition?.protein || 0}g`, dv: null },
                      ].map((item, idx) => (
                        <div key={idx} className={`grid grid-cols-3 gap-1 border-b border-black py-0.5 ${item.indent ? 'pl-2' : ''}`}>
                          <span className={item.indent ? '' : 'font-bold'}>{item.label} {item.val}</span>
                          <span className="text-right">{item.dv !== null ? `${item.dv}%` : ''}</span>
                          <span className="text-right">{item.dv !== null ? `${item.dv}%` : ''}</span>
                        </div>
                      ))}
                      <div className="text-[7px] pt-1">*%DV = % Daily Value based on 2,000 calorie diet</div>
                    </div>
                  ) : (
                    /* Standard Vertical Format Preview (default) */
                    <div className="border-2 border-black p-2 w-64 bg-white font-sans text-xs">
                      <div className="text-2xl font-black border-b-8 border-black pb-1">Nutrition Facts</div>
                      <div className="text-[10px] border-b border-black py-1">
                        <div className="flex justify-between font-bold">
                          <span>Serving size</span>
                          <span>{servingSize.description} ({Math.round(servingSize.grams)}g)</span>
                        </div>
                      </div>
                      <div className="border-b-4 border-black py-1">
                        <div className="text-[10px] font-bold">Amount per serving</div>
                        <div className="flex justify-between items-end">
                          <span className="text-xl font-black">Calories</span>
                          <span className="text-2xl font-black">
                            {nutritionData?.nutrition ? Math.round(nutritionData.nutrition.calories) : '---'}
                          </span>
                        </div>
                      </div>
                      <div className="text-[10px] text-right border-b border-black py-0.5 font-bold">% Daily Value*</div>
                      
                      {/* Macros */}
                      <div className="border-b border-black py-0.5">
                        <div className="flex justify-between">
                          <span><strong>Total Fat</strong> {nutritionData?.nutrition ? `${nutritionData.nutrition.total_fat}g` : '---'}</span>
                          <span className="font-bold">{nutritionData?.daily_values?.total_fat || 0}%</span>
                        </div>
                        <div className="flex justify-between pl-3">
                          <span>Saturated Fat {nutritionData?.nutrition ? `${nutritionData.nutrition.saturated_fat}g` : '---'}</span>
                          <span className="font-bold">{nutritionData?.daily_values?.saturated_fat || 0}%</span>
                        </div>
                        <div className="pl-3">
                          <span><em>Trans</em> Fat {nutritionData?.nutrition ? `${nutritionData.nutrition.trans_fat}g` : '---'}</span>
                        </div>
                      </div>
                      
                      <div className="flex justify-between border-b border-black py-0.5">
                        <span><strong>Cholesterol</strong> {nutritionData?.nutrition ? `${Math.round(nutritionData.nutrition.cholesterol)}mg` : '---'}</span>
                        <span className="font-bold">{nutritionData?.daily_values?.cholesterol || 0}%</span>
                      </div>
                      
                      <div className="flex justify-between border-b border-black py-0.5">
                        <span><strong>Sodium</strong> {nutritionData?.nutrition ? `${Math.round(nutritionData.nutrition.sodium)}mg` : '---'}</span>
                        <span className="font-bold">{nutritionData?.daily_values?.sodium || 0}%</span>
                      </div>
                      
                      <div className="border-b border-black py-0.5">
                        <div className="flex justify-between">
                          <span><strong>Total Carbohydrate</strong> {nutritionData?.nutrition ? `${nutritionData.nutrition.total_carbs}g` : '---'}</span>
                          <span className="font-bold">{nutritionData?.daily_values?.total_carbs || 0}%</span>
                        </div>
                        <div className="flex justify-between pl-3">
                          <span>Dietary Fiber {nutritionData?.nutrition ? `${nutritionData.nutrition.dietary_fiber}g` : '---'}</span>
                          <span className="font-bold">{nutritionData?.daily_values?.dietary_fiber || 0}%</span>
                        </div>
                        <div className="flex justify-between pl-3">
                          <span>Total Sugars {nutritionData?.nutrition ? `${nutritionData.nutrition.sugars}g` : '---'}</span>
                          <span></span>
                        </div>
                        <div className="flex justify-between pl-6 text-[9px]">
                          <span>Incl. {nutritionData?.nutrition ? `${nutritionData.nutrition.added_sugars}g` : '---'} Added Sugars</span>
                          <span className="font-bold">{nutritionData?.daily_values?.added_sugars || 0}%</span>
                        </div>
                      </div>
                      
                      <div className="flex justify-between border-b-4 border-black py-0.5">
                        <span><strong>Protein</strong> {nutritionData?.nutrition ? `${nutritionData.nutrition.protein}g` : '---'}</span>
                        <span></span>
                      </div>
                      
                      {/* Micronutrients */}
                      <div className="flex justify-between border-b border-black py-0.5">
                        <span>Vitamin D {nutritionData?.nutrition ? `${nutritionData.nutrition.vitamin_d}mcg` : '---'}</span>
                        <span className="font-bold">{nutritionData?.daily_values?.vitamin_d || 0}%</span>
                      </div>
                      <div className="flex justify-between border-b border-black py-0.5">
                        <span>Calcium {nutritionData?.nutrition ? `${Math.round(nutritionData.nutrition.calcium)}mg` : '---'}</span>
                        <span className="font-bold">{nutritionData?.daily_values?.calcium || 0}%</span>
                      </div>
                      <div className="flex justify-between border-b border-black py-0.5">
                        <span>Iron {nutritionData?.nutrition ? `${nutritionData.nutrition.iron}mg` : '---'}</span>
                        <span className="font-bold">{nutritionData?.daily_values?.iron || 0}%</span>
                      </div>
                      <div className="flex justify-between border-b border-black py-0.5">
                        <span>Potassium {nutritionData?.nutrition ? `${Math.round(nutritionData.nutrition.potassium)}mg` : '---'}</span>
                        <span className="font-bold">{nutritionData?.daily_values?.potassium || 0}%</span>
                      </div>
                      
                      <div className="text-[8px] pt-1 leading-tight">
                        *The % Daily Value tells you how much a nutrient in a serving of food contributes to a daily diet. 2,000 calories a day is used for general nutrition advice.
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    {selectedLabelFormat === "tabular" ? "Tabular Format" : 
                     selectedLabelFormat === "linear" ? "Linear Format" : 
                     selectedLabelFormat === "dual" ? "Dual Column Format" : 
                     "Standard Vertical Format"}
                  </p>
                </div>
                
                {/* Status Panel */}
                <div className="flex-1 space-y-4">
                  {nutritionLoading ? (
                    <div className="bg-gray-50 border rounded p-4 text-sm text-center">
                      <div className="animate-pulse">Calculating nutrition values...</div>
                    </div>
                  ) : nutritionData?.success && nutritionData.linked_count > 0 ? (
                    <div className={`border rounded p-3 text-sm ${nutritionData.linked_count === nutritionData.total_ingredients ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                      <p className={`font-medium ${nutritionData.linked_count === nutritionData.total_ingredients ? 'text-green-800' : 'text-yellow-800'}`}>
                        {nutritionData.linked_count === nutritionData.total_ingredients 
                          ? '✓ All ingredients linked to nutrition data' 
                          : `${nutritionData.linked_count} of ${nutritionData.total_ingredients} ingredients linked`}
                      </p>
                      {nutritionData.unlinked_ingredients?.length > 0 && (
                        <p className="text-yellow-700 text-xs mt-1">
                          Unlinked: {nutritionData.unlinked_ingredients.join(", ")}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm">
                      <p className="font-medium text-yellow-800">No nutrition data linked</p>
                      <p className="text-yellow-700 text-xs mt-1">
                        Add matching entries to the Nutrition Database to populate values.
                      </p>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs">Serving Size (g)</Label>
                      <Input 
                        type="number" 
                        value={servingSize.grams} 
                        onChange={(e) => handleServingSizeChange(parseFloat(e.target.value) || 0)} 
                        className="h-8" 
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Serving Description</Label>
                      <Input 
                        value={servingSize.description} 
                        onChange={(e) => setServingSize({ ...servingSize, description: e.target.value })} 
                        className="h-8" 
                      />
                    </div>
                  </div>
                  
                  {/* Quick Summary */}
                  {nutritionData?.nutrition && (
                    <div className="bg-gray-50 rounded p-3">
                      <Label className="text-xs font-medium text-gray-600">Per Serving Summary</Label>
                      <div className="grid grid-cols-4 gap-2 mt-2">
                        <div className="text-center p-2 bg-white rounded border">
                          <div className="text-lg font-bold">{Math.round(nutritionData.nutrition.calories)}</div>
                          <div className="text-[10px] text-gray-500">Calories</div>
                        </div>
                        <div className="text-center p-2 bg-white rounded border">
                          <div className="text-lg font-bold">{nutritionData.nutrition.protein}g</div>
                          <div className="text-[10px] text-gray-500">Protein</div>
                        </div>
                        <div className="text-center p-2 bg-white rounded border">
                          <div className="text-lg font-bold">{nutritionData.nutrition.total_carbs}g</div>
                          <div className="text-[10px] text-gray-500">Carbs</div>
                        </div>
                        <div className="text-center p-2 bg-white rounded border">
                          <div className="text-lg font-bold">{nutritionData.nutrition.total_fat}g</div>
                          <div className="text-[10px] text-gray-500">Fat</div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Label Format Selection */}
                  <div className="border-t pt-3">
                    <Label className="text-xs font-medium text-gray-600">Label Format</Label>
                    <div className="grid grid-cols-4 gap-2 mt-2">
                      {[
                        { id: "vertical", name: "Standard", desc: "Vertical" },
                        { id: "tabular", name: "Tabular", desc: "Side-by-side" },
                        { id: "linear", name: "Linear", desc: "Compact" },
                        { id: "dual", name: "Dual", desc: "Per container" }
                      ].map(format => (
                        <div 
                          key={format.id}
                          className={`border rounded p-2 cursor-pointer text-center transition-colors ${selectedLabelFormat === format.id ? 'border-purple-500 bg-purple-50' : 'hover:bg-gray-50'}`}
                          onClick={() => setSelectedLabelFormat(format.id)}
                        >
                          <div className="font-medium text-xs">{format.name}</div>
                          <div className="text-[10px] text-gray-500">{format.desc}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="linking" className="space-y-4">
              <div className="text-sm text-gray-600 mb-2">
                Ingredients are automatically matched to your Nutrition Database by name. Click "Search USDA" to quickly import nutrition data for unlinked ingredients.
              </div>
              <div className="border rounded overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Ingredient</th>
                      <th className="text-right px-3 py-2 font-medium">Grams</th>
                      <th className="text-left px-3 py-2 font-medium">Status</th>
                      <th className="text-left px-3 py-2 font-medium">Matched To / Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {formulationLines.map((line, idx) => {
                      const linkedDetail = nutritionData?.linked_details?.find(d => d.name === line.item_name);
                      const isLinked = !!linkedDetail;
                      const isSearching = usdaSearchIngredient === line.item_name;
                      
                      return (
                        <tr key={idx} className={isLinked ? 'bg-green-50' : 'bg-red-50'}>
                          <td className="px-3 py-2 font-medium">{line.item_name}</td>
                          <td className="px-3 py-2 text-right">{line.bom_qty_grams}g</td>
                          <td className="px-3 py-2">
                            {isLinked ? (
                              <Badge className="bg-green-100 text-green-700">Linked</Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-700">Not Found</Badge>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {linkedDetail ? (
                              <span className="text-gray-600">
                                {linkedDetail.matched_to}
                                <Badge className="ml-2 text-[10px]" variant="outline">{linkedDetail.source}</Badge>
                              </span>
                            ) : (
                              <div>
                                {isSearching ? (
                                  <div className="space-y-2">
                                    {usdaQuickLoading ? (
                                      <div className="flex items-center gap-2 text-xs text-gray-500">
                                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                                        Searching USDA...
                                      </div>
                                    ) : usdaQuickResults.length > 0 ? (
                                      <div className="space-y-1">
                                        <div className="text-xs text-gray-500 mb-1">Select to import:</div>
                                        {usdaQuickResults.map((result, rIdx) => (
                                          <div 
                                            key={rIdx}
                                            className="flex items-center justify-between gap-2 p-1.5 bg-white border rounded hover:bg-blue-50 cursor-pointer text-xs"
                                            onClick={() => handleUsdaQuickImport(result.fdcId, result.description)}
                                          >
                                            <span className="truncate flex-1" title={result.description}>
                                              {result.description.length > 40 ? result.description.substring(0, 40) + '...' : result.description}
                                            </span>
                                            {usdaImporting === result.fdcId ? (
                                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                                            ) : (
                                              <Badge className="bg-blue-100 text-blue-700 text-[10px]">Import</Badge>
                                            )}
                                          </div>
                                        ))}
                                        <Button 
                                          size="sm" 
                                          variant="ghost" 
                                          className="h-6 text-xs w-full"
                                          onClick={() => { setUsdaSearchIngredient(null); setUsdaQuickResults([]); }}
                                        >
                                          Cancel
                                        </Button>
                                      </div>
                                    ) : (
                                      <div className="text-xs text-gray-500">
                                        No results found.
                                        <Button 
                                          size="sm" 
                                          variant="ghost" 
                                          className="h-6 text-xs ml-2"
                                          onClick={() => { setUsdaSearchIngredient(null); setUsdaQuickResults([]); }}
                                        >
                                          Close
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="h-7 text-xs"
                                    onClick={() => handleUsdaQuickSearch(line.item_name)}
                                  >
                                    <Search className="w-3 h-3 mr-1" />
                                    Search USDA
                                  </Button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {nutritionData?.unlinked_ingredients?.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
                  <p className="font-medium text-blue-800">Quick Tip</p>
                  <p className="text-blue-700 text-xs mt-1">
                    Click "Search USDA" next to any unlinked ingredient to quickly find and import nutrition data from the USDA FoodData Central database.
                  </p>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="ingredients" className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Ingredient Statement</Label>
                <div className="bg-gray-50 border rounded p-3 text-sm max-h-40 overflow-y-auto">
                  <strong>INGREDIENTS:</strong> {generateIngredientStatement()}
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Allergen Statement</Label>
                <div className="bg-orange-50 border border-orange-200 rounded p-3 text-sm">
                  {generateAllergenStatement() || <span className="text-gray-500">No allergens declared.</span>}
                </div>
              </div>
            </TabsContent>
          </Tabs>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNutritionModal(false)}>Close</Button>
            <Button onClick={handleGeneratePDF}><Download className="w-4 h-4 mr-1" />Generate PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Action Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {reviewAction === 'approve' ? 'Approve Item' : 'Reject Item'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium">Notes (optional)</Label>
              <Textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder={reviewAction === 'approve' ? 'Add approval notes...' : 'Reason for rejection...'}
                rows={3}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowReviewModal(false); setReviewNotes(''); }}>
              Cancel
            </Button>
            {reviewAction === 'approve' ? (
              <Button className="bg-green-600 hover:bg-green-700" onClick={handleApproveItem}>
                <CheckCircle className="w-4 h-4 mr-1" />Approve
              </Button>
            ) : (
              <Button className="bg-red-600 hover:bg-red-700" onClick={handleRejectItem}>
                <XCircle className="w-4 h-4 mr-1" />Reject
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RDItemDetailPage;
